import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Modal from "../components/ui/Modal";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import {
  getBusinessJob,
  updateBusinessJob,
  activateBusinessJob,
  closeBusinessJob,
  deleteBusinessJob,
  listBusinessCandidates,
  addBusinessCandidates,
  inviteBusinessCandidates,
  getBusinessCandidate,
  resendBusinessCandidateInvite,
  withdrawBusinessCandidate,
  getBusinessRoundReview,
  confirmBusinessRound,
  advanceBusinessRound,
  getBusinessJobAnalytics,
  getBusinessCandidateReport,
  advanceBusinessCandidate,
  createBusinessJobRound,
  updateBusinessJobRound,
  deleteBusinessJobRound,
  listBusinessInterviewTypes,
  listBusinessTopics,
  listQuestionBank,
} from "../api/client";

// JobDetail — the recruiter's cockpit for one opening. Shows the job's schedule
// and interview rounds, and manages the candidate funnel end to end: add (no
// credit), invite (reserves a wallet credit + emails a tokenized link), resend,
// view per-round results, and withdraw (releases an unstarted credit). Lifecycle
// controls — activate, close, delete — live in the header. Every write is gated
// to owner/admin/recruiter; viewers see a read-only funnel. Candidates never see
// their own scores anywhere; scores shown here are the recruiter-only view.

const WRITER_ROLES = ["owner", "admin", "recruiter"];
const PAGE_SIZE = 50;

const JOB_STATUS_BADGE = {
  draft: "bg-muted text-muted-foreground",
  active: "bg-success/15 text-success",
  closed: "bg-warning/15 text-warning",
  archived: "bg-muted text-muted-foreground",
};

const ROUND_STATUS_BADGE = {
  pending: "bg-muted text-muted-foreground",
  open: "bg-success/15 text-success",
  awaiting_review: "bg-warning/15 text-warning",
  closed: "bg-warning/15 text-warning",
  completed: "bg-primary/10 text-primary",
};

const INVITE_STATUS_BADGE = {
  pending: "bg-muted text-muted-foreground",
  invited: "bg-primary/10 text-primary",
  completed: "bg-success/15 text-success",
  rejected: "bg-destructive/10 text-destructive",
  withdrawn: "bg-muted text-muted-foreground",
};

const ROUND_ASSIGN_BADGE = {
  invited: "bg-primary/10 text-primary",
  in_progress: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
  passed: "bg-success/15 text-success",
  failed: "bg-destructive/10 text-destructive",
  expired: "bg-muted text-muted-foreground",
  cancelled: "bg-muted text-muted-foreground",
  rejected: "bg-destructive/10 text-destructive",
  advanced: "bg-success/15 text-success",
};

const ADVANCE_RULE_LABEL = {
  cutoff: (v) => `Advance candidates scoring ≥ ${v ?? 0}`,
  top_n: (v) => `Advance the top ${v ?? 0} candidates`,
  top_percent: (v) => `Advance the top ${v ?? 0}%`,
  none: () => "Advance everyone",
};

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleCase(s) {
  return String(s || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

// Score → semantic tone for the recruiter report (0-100 competency scale).
function scoreTextTone(score) {
  if (score == null || Number.isNaN(score)) return "text-muted-foreground";
  if (score >= 75) return "text-success";
  if (score >= 50) return "text-warning";
  return "text-destructive";
}

function scoreBarTone(score) {
  if (score == null || Number.isNaN(score)) return "bg-muted-foreground/40";
  if (score >= 75) return "bg-success";
  if (score >= 50) return "bg-warning";
  return "bg-destructive";
}

function clampPct(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(100, v));
}

function Badge({ className, children, dot }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize " +
        (className || "bg-muted text-muted-foreground")
      }
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

// Parses the bulk "Name, email, phone" textarea into candidate rows. Blank lines
// are ignored; per-row validation is left to the backend, which reports invalids.
function parseCandidateLines(text) {
  return String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(",").map((p) => p.trim());
      const [name, email, phone] = parts;
      const row = { name: name || "", email: email || "" };
      if (phone) row.phone = phone;
      return row;
    });
}

// ── Add-candidates modal ─────────────────────────────────────────────────────
function AddCandidatesModal({ open, onClose, jobId, onAdded }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  const reset = () => {
    setText("");
    setError("");
    setSummary(null);
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const submit = async () => {
    setError("");
    setSummary(null);
    const candidates = parseCandidateLines(text);
    if (candidates.length === 0) {
      setError("Add at least one candidate (Name, email per line).");
      return;
    }
    setBusy(true);
    try {
      const res = await addBusinessCandidates(jobId, candidates);
      setSummary(res);
      if (res.inserted > 0) onAdded(res);
    } catch (err) {
      setError(err?.error || "Could not add candidates.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add candidates"
      description="Adding records candidates on the job. No credits are spent and no emails go out until you invite them."
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={close} disabled={busy}>
            {summary ? "Done" : "Cancel"}
          </Button>
          <Button iconName="UserPlus" onClick={submit} loading={busy} disabled={busy}>
            Add candidates
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-foreground">Candidates</label>
          <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
            One per line: <code className="rounded bg-muted px-1">Name, email, phone</code> — phone
            optional.
          </p>
          <textarea
            className="flex min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            placeholder={"Ada Lovelace, ada@example.com\nAlan Turing, alan@example.com, +1 555 0100"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={busy}
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <Icon name="AlertCircle" size={15} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {summary && (
          <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4 text-sm">
            <div className="flex flex-wrap gap-4">
              <span className="inline-flex items-center gap-1.5 text-success">
                <Icon name="CheckCircle2" size={15} /> {summary.inserted} added
              </span>
              {summary.skippedDuplicate > 0 && (
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Icon name="Copy" size={15} /> {summary.skippedDuplicate} already on the job
                </span>
              )}
              {summary.invalid?.length > 0 && (
                <span className="inline-flex items-center gap-1.5 text-warning">
                  <Icon name="AlertTriangle" size={15} /> {summary.invalid.length} skipped
                </span>
              )}
            </div>
            {summary.invalid?.length > 0 && (
              <ul className="space-y-1 border-t border-border pt-2 text-xs text-muted-foreground">
                {summary.invalid.slice(0, 8).map((r, i) => (
                  <li key={i}>
                    Row {r.row}
                    {r.email ? ` (${r.email})` : ""}: {r.reason}
                  </li>
                ))}
                {summary.invalid.length > 8 && <li>…and {summary.invalid.length - 8} more</li>}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Candidate detail modal ───────────────────────────────────────────────────
function CandidateDetailModal({ open, onClose, jobId, candidateId, job, canWrite, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // When set, the modal shows that round's full report instead of the round
  // list (master → detail within one modal, so we never stack two modals).
  const [reportRound, setReportRound] = useState(null);
  // Manual-advance override state. The confirm is rendered INLINE (not a nested
  // Modal) so it never stacks on top of this modal's global Escape handling.
  const [advanceState, setAdvanceState] = useState("idle"); // idle | confirm | busy
  const [advanceErr, setAdvanceErr] = useState("");
  const [advanceMsg, setAdvanceMsg] = useState("");

  const loadCandidate = useCallback(async () => {
    setLoading(true);
    setError("");
    setData(null);
    try {
      const res = await getBusinessCandidate(jobId, candidateId);
      return res;
    } catch (err) {
      setError(err?.error || "Could not load the candidate.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [jobId, candidateId]);

  useEffect(() => {
    if (!open || !candidateId) return;
    let alive = true;
    setReportRound(null);
    setAdvanceState("idle");
    setAdvanceErr("");
    setAdvanceMsg("");
    (async () => {
      const res = await loadCandidate();
      if (alive && res) setData(res);
    })();
    return () => {
      alive = false;
    };
  }, [open, candidateId, loadCandidate]);

  const handleClose = () => {
    setReportRound(null);
    setAdvanceState("idle");
    onClose?.();
  };

  // The candidate's furthest round. Manual advance is only offered here — and only
  // when it is RESOLVED — so we never show it on a round the candidate has already
  // moved past (their furthest slot would then be a later, still-open one).
  const RESOLVED_STATUSES = ["completed", "passed", "failed", "expired"];
  const lastRound = useMemo(() => {
    const rs = data?.rounds || [];
    if (!rs.length) return null;
    return rs.reduce((a, b) => (b.roundNumber > a.roundNumber ? b : a));
  }, [data]);

  const isFinalRound = !!(lastRound && job?.numRounds && lastRound.roundNumber >= job.numRounds);
  const nextRoundMeta = useMemo(() => {
    if (!lastRound || isFinalRound) return null;
    return (job?.rounds || []).find((r) => r.roundNumber === lastRound.roundNumber + 1) || null;
  }, [lastRound, isFinalRound, job]);

  const canAdvanceCandidate =
    canWrite &&
    job?.mode === "pipeline" &&
    job?.status === "active" &&
    !!lastRound &&
    RESOLVED_STATUSES.includes(lastRound.status) &&
    !["advanced", "hired", "withdrawn"].includes(data?.status);

  const advanceLabel = isFinalRound
    ? "Select as finalist"
    : `Advance to ${nextRoundMeta ? `Round ${nextRoundMeta.roundNumber}` : "next round"}`;

  const runAdvance = async () => {
    if (!lastRound) return;
    setAdvanceState("busy");
    setAdvanceErr("");
    setAdvanceMsg("");
    try {
      const res = await advanceBusinessCandidate(jobId, candidateId, lastRound.roundNumber);
      let msg;
      if (res?.status === "finalized") {
        msg = `${res.candidateName || "Candidate"} marked as a finalist.`;
      } else if (res?.status === "already_advanced") {
        msg = `Already advanced to ${res.nextRoundName || "the next round"} — no credit used.`;
      } else {
        msg = `Advanced to ${res?.nextRoundName || "the next round"}${
          res?.walletRemaining != null ? ` · ${res.walletRemaining} credit${res.walletRemaining === 1 ? "" : "s"} left` : ""
        }.`;
      }
      const refreshed = await loadCandidate();
      if (refreshed) setData(refreshed);
      setAdvanceState("idle");
      setAdvanceMsg(msg);
      onChanged?.();
    } catch (err) {
      setAdvanceErr(
        err?.insufficientCredits
          ? `${err.error || "Not enough credits."} Buy more credits to advance this candidate.`
          : err?.error || "Could not advance this candidate."
      );
      setAdvanceState("confirm");
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={reportRound ? reportRound.roundName || "Report" : data?.name || "Candidate"}
      description={reportRound ? `${data?.name || "Candidate"} · ${reportRound.interviewType || ""}` : data?.email}
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          {reportRound ? (
            <Button variant="outline" size="sm" iconName="ArrowLeft" onClick={() => setReportRound(null)}>
              Back to rounds
            </Button>
          ) : (
            <span />
          )}
          <Button variant="ghost" onClick={handleClose}>
            Close
          </Button>
        </div>
      }
    >
      {reportRound ? (
        <CandidateReportPanel
          jobId={jobId}
          candidateId={candidateId}
          round={reportRound}
        />
      ) : loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-muted/50" />
          ))}
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <Icon name="AlertCircle" size={15} />
          <span>{error}</span>
        </div>
      ) : data ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={INVITE_STATUS_BADGE[data.inviteStatus]}>
              {titleCase(data.inviteStatus)}
            </Badge>
            {data.phone && (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon name="Phone" size={13} /> {data.phone}
              </span>
            )}
            <span className="text-xs text-muted-foreground">Added {fmtDate(data.createdAt)}</span>
          </div>

          {advanceMsg && (
            <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-sm text-success">
              <Icon name="CheckCircle2" size={15} />
              <span>{advanceMsg}</span>
            </div>
          )}

          {advanceState !== "idle" && lastRound && (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-3">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} className="mt-0.5 flex-shrink-0 text-warning" />
                <div className="min-w-0 text-sm text-foreground">
                  <p className="font-medium">
                    {isFinalRound
                      ? `Select ${data?.name || "this candidate"} as a finalist?`
                      : `Advance ${data?.name || "this candidate"} to ${
                          nextRoundMeta
                            ? `Round ${nextRoundMeta.roundNumber}${nextRoundMeta.name ? ` · ${nextRoundMeta.name}` : ""}`
                            : "the next round"
                        }?`}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {isFinalRound
                      ? `This overrides ${lastRound.roundName}'s result and marks them selected. No interview credit is used.`
                      : `This overrides ${lastRound.roundName}'s result — even a failed one — and invites them to the next round. It uses 1 interview credit.`}
                  </p>
                  {advanceErr && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-destructive">
                      <Icon name="AlertCircle" size={13} /> {advanceErr}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAdvanceState("idle")}
                  disabled={advanceState === "busy"}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  iconName="ArrowRight"
                  loading={advanceState === "busy"}
                  onClick={runAdvance}
                >
                  {isFinalRound ? "Select as finalist" : "Advance & use 1 credit"}
                </Button>
              </div>
            </div>
          )}

          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Round history
            </h4>
            {data.rounds?.length ? (
              <ol className="space-y-2">
                {data.rounds.map((r) => (
                  <li
                    key={r.roundNumber}
                    className="rounded-lg border border-border bg-background p-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {r.roundName}
                          </span>
                          <Badge className={ROUND_ASSIGN_BADGE[r.status]}>
                            {titleCase(r.status)}
                          </Badge>
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {r.interviewType}
                          {r.attemptsUsed > 0 &&
                            ` · ${r.attemptsUsed} attempt${r.attemptsUsed === 1 ? "" : "s"}`}
                        </div>
                      </div>
                      {r.score != null && (
                        <div className="flex-shrink-0 text-right">
                          <div className="text-lg font-semibold tabular-nums text-foreground">
                            {r.score}
                          </div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            score
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                      {r.invitedAt && <span>Invited {fmtDate(r.invitedAt)}</span>}
                      {r.startedAt && <span>Started {fmtDateTime(r.startedAt)}</span>}
                      {r.completedAt && <span>Completed {fmtDateTime(r.completedAt)}</span>}
                      {r.dueAt && <span>Due {fmtDate(r.dueAt)}</span>}
                    </div>
                    {(() => {
                      const showReport =
                        r.score != null ||
                        r.startedAt ||
                        r.status === "completed" ||
                        r.status === "advanced" ||
                        r.status === "in_progress";
                      const showAdvance =
                        canAdvanceCandidate &&
                        lastRound &&
                        r.roundNumber === lastRound.roundNumber;
                      if (!showReport && !showAdvance) return null;
                      return (
                        <div className="mt-2 flex flex-wrap items-center justify-end gap-2 border-t border-border/60 pt-2">
                          {showReport && (
                            <Button
                              variant="outline"
                              size="xs"
                              iconName="FileText"
                              onClick={() => setReportRound(r)}
                            >
                              View report
                            </Button>
                          )}
                          {showAdvance && (
                            <Button
                              variant="success"
                              size="xs"
                              iconName="ArrowRight"
                              onClick={() => {
                                setAdvanceErr("");
                                setAdvanceMsg("");
                                setAdvanceState("confirm");
                              }}
                            >
                              {advanceLabel}
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
                No interview activity yet. The candidate appears here once invited.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

// ── Recruiter-only candidate report panel ────────────────────────────────────
// Renders ONE candidate's full stored interview evaluation for a given round,
// plus the synthesized brief summary. Shown only to signed-in org members inside
// the console — the candidate has no path to this anywhere (their portal is
// token-scoped and never returns a score or report). The report lifecycle is
// handled explicitly: loading, ready, still-generating (202), not-yet-started
// (409), and foreign/unknown attempt (404).
function CandidateReportPanel({ jobId, candidateId, round }) {
  const [phase, setPhase] = useState("loading");
  const [payload, setPayload] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (!candidateId || !round) return;
    let alive = true;
    setPhase("loading");
    setPayload(null);
    setErrMsg("");
    (async () => {
      try {
        const res = await getBusinessCandidateReport(jobId, candidateId, round.roundNumber);
        if (!alive) return;
        if (res?.status === "generating") {
          setPhase("generating");
          setPayload(res);
        } else {
          setPhase("ready");
          setPayload(res);
        }
      } catch (err) {
        if (!alive) return;
        if (err?.notStarted) setPhase("not_started");
        else if (err?.notFound) setPhase("not_found");
        else {
          setPhase("error");
          setErrMsg(err?.error || "Could not load the report.");
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [jobId, candidateId, round]);

  if (phase === "loading") {
    return (
      <div className="space-y-3">
        <div className="h-20 animate-pulse rounded-lg bg-muted/50" />
        <div className="h-32 animate-pulse rounded-lg bg-muted/50" />
        <div className="h-24 animate-pulse rounded-lg bg-muted/50" />
      </div>
    );
  }

  if (phase === "generating") {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-4 py-10 text-center">
        <Icon name="Loader" size={22} className="text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">The report is still being prepared</p>
        <p className="max-w-sm text-xs text-muted-foreground">
          {payload?.message ||
            "This interview has finished but its evaluation isn't ready yet. Check back in a little while."}
        </p>
      </div>
    );
  }

  if (phase === "not_started") {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-4 py-10 text-center">
        <Icon name="Hourglass" size={22} className="text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">No report yet</p>
        <p className="max-w-sm text-xs text-muted-foreground">
          This candidate hasn&apos;t started this round&apos;s interview. A report appears here once
          they complete it.
        </p>
      </div>
    );
  }

  if (phase === "not_found") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        <Icon name="AlertCircle" size={15} />
        <span>No interview was found for this candidate round.</span>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        <Icon name="AlertCircle" size={15} />
        <span>{errMsg}</span>
      </div>
    );
  }

  // phase === "ready"
  const report = payload?.report || {};
  const summary = (payload?.summary || "").trim();
  const overall = report.overallData || {};
  const parameters = Array.isArray(report.parameters) ? report.parameters : [];
  const strengths = Array.isArray(report.strengths) ? report.strengths : [];
  const weaknesses = Array.isArray(report.weaknesses) ? report.weaknesses : [];
  // const recommendations = Array.isArray(report.recommendations) ? report.recommendations : [];
  const problemReviews = Array.isArray(report.problemReviews) ? report.problemReviews : [];
  const transcript = Array.isArray(report.transcript) ? report.transcript : [];
  const codeHistory = Array.isArray(report.code) ? report.code : [];
  const codeSnippets = Array.isArray(report.codeSnippets) ? report.codeSnippets : [];
  const overallScore = typeof overall.score === "number" ? overall.score : null;

  return (
    <div className="space-y-6">
      {/* Brief summary — the recruiter's at-a-glance takeaway. */}
      {summary && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
            <Icon name="Sparkles" size={13} /> Summary
          </div>
          <p className="text-sm leading-relaxed text-foreground">{summary}</p>
        </div>
      )}

      {/* Overall score header. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-border bg-background p-4">
        <div className="flex items-baseline gap-1">
          <span className={"text-3xl font-bold tabular-nums " + scoreTextTone(overallScore)}>
            {overallScore != null ? overallScore : "—"}
          </span>
          <span className="text-sm text-muted-foreground">/ 100</span>
        </div>
        {overall.grade && (
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Grade</span>
            <span className="text-sm font-semibold text-foreground">{overall.grade}</span>
          </div>
        )}
        {overall.duration != null && (
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Duration</span>
            <span className="text-sm font-semibold text-foreground">{overall.duration} min</span>
          </div>
        )}
        {overall.date && (
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Taken</span>
            <span className="text-sm font-semibold text-foreground">{overall.date}</span>
          </div>
        )}
        {overall.verdict && (
          <div className="w-full text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Verdict: </span>
            {overall.verdict}
          </div>
        )}
      </div>

      {/* Per-competency breakdown. */}
      {parameters.length > 0 && (
        <div>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Performance breakdown
          </h4>
          <div className="space-y-3">
            {parameters.map((p, i) => {
              const sc = typeof p.score === "number" ? p.score : null;
              return (
                <div key={p.id ?? p.name ?? i} className="space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-foreground">{p.name}</span>
                    <span className={"text-sm font-semibold tabular-nums " + scoreTextTone(sc)}>
                      {sc != null ? sc : "—"}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={"h-full rounded-full " + scoreBarTone(sc)}
                      style={{ width: `${clampPct(sc)}%` }}
                    />
                  </div>
                  {p.feedback && (
                    <p className="text-xs leading-relaxed text-muted-foreground">{p.feedback}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Strengths / weaknesses. */}
      {(strengths.length > 0 || weaknesses.length > 0) && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-success">
              <Icon name="TrendingUp" size={13} /> Strengths
            </h4>
            {strengths.length ? (
              <ul className="space-y-2">
                {strengths.map((s, i) => (
                  <li key={s.id ?? i} className="rounded-lg border border-success/20 bg-success/5 p-3">
                    <div className="text-sm font-medium text-foreground">{s.title}</div>
                    {s.description && (
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        {s.description}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">None recorded.</p>
            )}
          </div>
          <div>
            <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-warning">
              <Icon name="TrendingDown" size={13} /> Areas to improve
            </h4>
            {weaknesses.length ? (
              <ul className="space-y-2">
                {weaknesses.map((w, i) => (
                  <li key={w.id ?? i} className="rounded-lg border border-warning/20 bg-warning/5 p-3">
                    <div className="text-sm font-medium text-foreground">{w.title}</div>
                    {(w.description || w.suggestion) && (
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        {w.description || w.suggestion}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">None recorded.</p>
            )}
          </div>
        </div>
      )}

      {/* Recommendations. */}
      {/* {recommendations.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Recommendations
          </h4>
          <ul className="space-y-2">
            {recommendations.map((rec, i) => (
              <li key={rec.id ?? i} className="rounded-lg border border-border bg-background p-3">
                <div className="text-sm font-medium text-foreground">{rec.title}</div>
                {rec.description && (
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {rec.description}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )} */}

      {/* Problem reviews — the substantive record of what the candidate did:
          each problem attempted, the rating and feedback, and their code. */}
      {problemReviews.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Problems &amp; responses
          </h4>
          <div className="space-y-3">
            {problemReviews.map((pr, i) => (
              <div key={i} className="rounded-lg border border-border bg-background p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {pr.problemTitle || `Problem ${i + 1}`}
                  </span>
                  <div className="flex items-center gap-2">
                    {pr.difficulty && (
                      <Badge className="bg-muted text-muted-foreground">{pr.difficulty}</Badge>
                    )}
                    {pr.rating != null && pr.rating !== "" && (
                      <span className="text-xs font-medium text-muted-foreground">
                        Rating: {pr.rating}
                      </span>
                    )}
                  </div>
                </div>
                {pr.feedback && (
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{pr.feedback}</p>
                )}
                {pr.code && (
                  <pre className="mt-2 max-h-72 overflow-auto rounded-md bg-muted/60 p-3 text-[11px] leading-relaxed text-foreground">
                    <code>{pr.code}</code>
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(codeHistory.length > 0 || codeSnippets.length > 0) && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Code history
          </h4>
          <div className="space-y-3">
            {(codeHistory.length ? codeHistory : codeSnippets).map((entry, i) => {
              const code = entry?.code || entry?.content || "";
              const language = entry?.language || entry?.lang || "";
              return (
                <div key={i} className="overflow-hidden rounded-lg border border-border bg-background">
                  <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs text-muted-foreground">
                    <span>{language || `Snapshot ${i + 1}`}</span>
                    {entry?.ts && <span>{fmtDateTime(new Date(entry.ts * 1000).toISOString())}</span>}
                  </div>
                  <pre className="max-h-80 overflow-auto bg-muted/50 p-3 text-[11px] leading-relaxed text-foreground">
                    <code>{code || JSON.stringify(entry, null, 2)}</code>
                  </pre>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {transcript.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Transcript
          </h4>
          <div className="max-h-96 space-y-2 overflow-y-auto rounded-lg border border-border bg-background p-3">
            {transcript.map((line, i) => {
              const speaker = line?.speaker || line?.role || line?.from || "Speaker";
              const text = line?.text || line?.content || line?.message || "";
              return (
                <div key={i} className="rounded-md bg-muted/40 px-3 py-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {speaker}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-foreground">
                    {text || JSON.stringify(line, null, 2)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Nothing scored yet but the report row exists — a soft catch-all so the
          panel never renders empty. */}
      {!summary &&
        overallScore == null &&
        parameters.length === 0 &&
        strengths.length === 0 &&
        weaknesses.length === 0 &&
        problemReviews.length === 0 &&
        transcript.length === 0 &&
        codeHistory.length === 0 &&
        codeSnippets.length === 0 && (
          <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
            The report has no scored content yet.
          </p>
        )}
    </div>
  );
}

// ── Confirm dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ open, onClose, title, description, confirmLabel, variant, busy, onConfirm }) {
  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant={variant || "default"} onClick={onConfirm} loading={busy} disabled={busy}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <span />
    </Modal>
  );
}

// ── Round review modal (manual gate) ─────────────────────────────────────────
// Opened for a round parked in "awaiting_review": shows the ranked completed
// candidates and the system's suggested pass set (pre-selected here), and lets
// the recruiter confirm or override exactly who advances. Confirming closes the
// round and opens the next one (or names finalists on the last round). No-show
// credits were already released when the round was parked, so nothing here
// touches the wallet except reserving one credit per advancing candidate. A
// candidate's invite token is NEVER shown.
function RoundReviewModal({ open, onClose, jobId, roundId, onConfirmed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !roundId) return;
    let alive = true;
    setLoading(true);
    setError("");
    setData(null);
    setSelected(new Set());
    (async () => {
      try {
        const res = await getBusinessRoundReview(jobId, roundId);
        if (!alive) return;
        setData(res);
        // Pre-select the system's suggestion so "Confirm" with no edits accepts it.
        const initial = new Set(
          (res.candidates || []).filter((c) => c.suggested === "pass").map((c) => c.candidateId)
        );
        setSelected(initial);
      } catch (err) {
        if (alive) setError(err?.error || "Could not load the round for review.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, jobId, roundId]);

  const candidates = data?.candidates || [];
  const isLast = !!data?.isLastRound;
  const ruleLabel = data
    ? (ADVANCE_RULE_LABEL[data.advanceRuleType] || ADVANCE_RULE_LABEL.none)(data.advanceRuleValue)
    : "";

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(candidates.map((c) => c.candidateId)));
  const clearAll = () => setSelected(new Set());
  const resetSuggested = () =>
    setSelected(new Set(candidates.filter((c) => c.suggested === "pass").map((c) => c.candidateId)));

  const suggestedIds = useMemo(
    () => new Set(candidates.filter((c) => c.suggested === "pass").map((c) => c.candidateId)),
    [candidates]
  );
  const isOverridden = useMemo(() => {
    if (selected.size !== suggestedIds.size) return true;
    for (const id of selected) if (!suggestedIds.has(id)) return true;
    return false;
  }, [selected, suggestedIds]);

  // Client-side score distribution of the completed candidates, so the recruiter
  // can see the shape of the field (and where a sensible cutoff falls) while
  // deciding who advances. 10 fixed bands; the last band includes 100.
  const scoreHistogram = useMemo(() => {
    const buckets = Array.from({ length: 10 }, (_, i) => ({
      label: i === 9 ? "90–100" : `${i * 10}–${i * 10 + 9}`,
      count: 0,
    }));
    let scored = 0;
    for (const c of candidates) {
      if (c.score == null) continue;
      const idx = Math.min(9, Math.max(0, Math.floor(c.score / 10)));
      buckets[idx].count += 1;
      scored += 1;
    }
    return { buckets, scored, max: Math.max(...buckets.map((b) => b.count), 1) };
  }, [candidates]);

  const confirm = async () => {
    setSubmitting(true);
    setError("");
    try {
      // Always send the explicit selection so the outcome matches exactly what
      // the recruiter sees (intersected server-side with who completed).
      const res = await confirmBusinessRound(jobId, roundId, Array.from(selected));
      onConfirmed?.(res);
    } catch (err) {
      if (err?.conflict) {
        // Round already resolved (cron or another recruiter) — let the parent
        // close this modal and refresh the timeline instead of showing a dead end.
        onConfirmed?.(null, err);
        return;
      }
      setError(err?.error || "Could not confirm the round.");
    } finally {
      setSubmitting(false);
    }
  };

  const advanceVerb = isLast ? "finalist" : "advance";
  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={data ? `Review ${data.roundName}` : "Review round"}
      description={
        isLast
          ? "Choose who becomes a finalist. Cleared candidates are marked not selected."
          : "Choose who advances to the next round. Cleared candidates are eliminated."
      }
      size="xl"
      footer={
        <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {selected.size} of {candidates.length} selected to {advanceVerb}
            {isOverridden && <span className="ml-1 text-warning">· overriding suggestion</span>}
          </span>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              iconName={isLast ? "Trophy" : "ArrowRightCircle"}
              onClick={confirm}
              loading={submitting}
              disabled={submitting || loading || !!error && !data}
            >
              {isLast ? "Confirm finalists" : "Confirm & advance"}
            </Button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted/50" />
          ))}
        </div>
      ) : error && !data ? (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <Icon name="AlertCircle" size={15} />
          <span>{error}</span>
        </div>
      ) : data ? (
        <div className="space-y-4">
          {/* Rule + suggestion summary */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Icon name="Filter" size={14} className="text-primary" />
              {isLast ? "Final round" : ruleLabel}
            </span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Icon name="Users" size={14} /> {data.completedCount} completed
            </span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Icon name="Sparkles" size={14} /> Suggests {data.suggestedPassCount} to {advanceVerb}
            </span>
          </div>

          {/* Score distribution of completed candidates (helps place a cutoff) */}
          {scoreHistogram.scored > 0 && (
            <div className="rounded-lg border border-border bg-background px-4 py-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
                  <Icon name="BarChart3" size={14} className="text-primary" /> Score distribution
                </span>
                <span className="text-[11px] text-muted-foreground">{scoreHistogram.scored} scored</span>
              </div>
              <div className="flex h-16 items-end gap-1">
                {scoreHistogram.buckets.map((b) => (
                  <div key={b.label} className="group relative flex flex-1 flex-col items-center gap-1">
                    <div
                      className="w-full rounded-t-sm bg-primary/25 transition-colors group-hover:bg-primary"
                      style={{ height: `${(b.count / scoreHistogram.max) * 100}%`, minHeight: b.count > 0 ? "3px" : "0" }}
                    />
                    <div className="w-full truncate text-center text-[9px] text-muted-foreground">{b.label}</div>
                    {b.count > 0 && (
                      <div className="absolute -top-5 hidden rounded bg-foreground px-1.5 py-0.5 text-[10px] text-background opacity-90 group-hover:block">
                        {b.count}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bulk actions */}
          {candidates.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <button
                type="button"
                onClick={selectAll}
                className="rounded-md border border-border px-2.5 py-1 font-medium text-muted-foreground hover:bg-muted"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="rounded-md border border-border px-2.5 py-1 font-medium text-muted-foreground hover:bg-muted"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={resetSuggested}
                disabled={!isOverridden}
                className="rounded-md border border-border px-2.5 py-1 font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                Reset to suggestion
              </button>
            </div>
          )}

          {/* Ranked candidate list */}
          {candidates.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-3 py-10 text-center text-sm text-muted-foreground">
              No candidate completed this round. Confirming will close it and advance no one.
            </p>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
              {candidates.map((c, idx) => {
                const isSel = selected.has(c.candidateId);
                return (
                  <li
                    key={c.candidateId}
                    className={
                      "flex items-center gap-3 px-3 py-2.5 transition-colors " +
                      (isSel ? "bg-success/5" : "bg-background")
                    }
                  >
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggle(c.candidateId)}
                        className="h-4 w-4 flex-shrink-0 rounded border-input text-primary focus:ring-2 focus:ring-ring"
                      />
                      <span className="w-5 flex-shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                        {idx + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {c.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">{c.email}</span>
                      </span>
                    </label>
                    <div className="flex flex-shrink-0 items-center gap-3">
                      {c.suggested === "pass" ? (
                        <span className="hidden rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary sm:inline">
                          Suggested
                        </span>
                      ) : null}
                      {c.attemptsUsed > 1 && (
                        <span className="hidden text-[11px] text-muted-foreground sm:inline">
                          {c.attemptsUsed} attempts
                        </span>
                      )}
                      <div className="w-10 text-right">
                        <span className="text-base font-semibold tabular-nums text-foreground">
                          {c.score != null ? c.score : <span className="text-muted-foreground">—</span>}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {error && data && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <Icon name="AlertCircle" size={15} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}

// ── Helpers for redesigned UI ──────────────────────────────────────────────
const AVATAR_BG_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
  "bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300",
  "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
];

function getAvatarColor(str = "") {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_BG_COLORS[Math.abs(hash) % AVATAR_BG_COLORS.length];
}

function getInitials(name = "") {
  const parts = String(name || "").trim().split(/\s+/);
  if (!parts.length || !parts[0]) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getRoundIconName(type = "") {
  const t = String(type || "").toLowerCase();
  if (t.includes("code") || t.includes("technical") || t.includes("algo") || t.includes("dsa") || t.includes("program")) return "Code2";
  if (t.includes("system") || t.includes("design") || t.includes("architecture")) return "LayoutGrid";
  if (t.includes("behavior") || t.includes("hr") || t.includes("final") || t.includes("mixed") || t.includes("culture")) return "Users";
  return "Layers";
}

function getAdvanceBadgeLabel(round) {
  if (!round) return "Next round";
  if (round.advanceRuleType === "cutoff") return `Score ≥ ${round.advanceRuleValue ?? 60}`;
  if (round.advanceRuleType === "top_n") return `Top ${round.advanceRuleValue ?? 10}`;
  if (round.advanceRuleType === "top_percent") return `Top ${round.advanceRuleValue ?? 20}%`;
  if (round.advanceRuleType === "none") return "Advance all";
  return "Next round";
}

function getCandidateRoundAssignment(candidate, round) {
  if (!candidate || !round) return null;
  const assignments = Array.isArray(candidate.roundAssignments) ? candidate.roundAssignments : [];
  return (
    assignments.find(
      (assignment) =>
        assignment.roundId === round.id ||
        Number(assignment.roundNumber) === Number(round.roundNumber)
    ) || null
  );
}

// ── Edit Job Modal ───────────────────────────────────────────────────────────
function EditJobModal({ open, onClose, job, onSaved }) {
  const [title, setTitle] = useState(job?.title || "");
  const [department, setDepartment] = useState(job?.department || "");
  const [location, setLocation] = useState(job?.location || "");
  const [deadlineAt, setDeadlineAt] = useState(job?.deadlineAt ? job.deadlineAt.slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (job && open) {
      setTitle(job.title || "");
      setDepartment(job.department || "");
      setLocation(job.location || "");
      setDeadlineAt(job.deadlineAt ? job.deadlineAt.slice(0, 10) : "");
      setError("");
    }
  }, [job, open]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) {
      setError("Job title is required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        title: title.trim(),
        department: department.trim() || undefined,
        location: location.trim() || undefined,
        deadlineAt: deadlineAt ? new Date(deadlineAt).toISOString() : undefined,
      };
      const res = await updateBusinessJob(job.id, payload);
      onSaved(res);
      onClose();
    } catch (err) {
      setError(err?.error || "Could not update the job.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Edit job details"
      description="Update the basic information and deadline for this opening."
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            className="bg-emerald-700 text-white hover:bg-emerald-800"
            onClick={handleSubmit}
            loading={busy}
            disabled={busy}
          >
            Save changes
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-foreground">Job title</label>
          <Input
            className="mt-1"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Senior Software Engineer"
            disabled={busy}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-foreground">Department</label>
            <Input
              className="mt-1"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="e.g. Engineering"
              disabled={busy}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-foreground">Location</label>
            <Input
              className="mt-1"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Remote / San Francisco"
              disabled={busy}
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-foreground">Application deadline</label>
          <input
            type="date"
            className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={deadlineAt}
            onChange={(e) => setDeadlineAt(e.target.value)}
            disabled={busy}
          />
        </div>
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <Icon name="AlertCircle" size={14} />
            <span>{error}</span>
          </div>
        )}
      </form>
    </Modal>
  );
}

// ── Invite Candidates Modal ─────────────────────────────────────────────────
function InviteCandidatesModal({ open, onClose, jobId, candidates, onInvited, onOpenAdd }) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pendingCandidates = useMemo(
    () => (candidates || []).filter((c) => c.inviteStatus === "pending"),
    [candidates]
  );

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(pendingCandidates.map((c) => c.id)));
      setError("");
    }
  }, [open, pendingCandidates]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => setSelectedIds(new Set(pendingCandidates.map((c) => c.id)));
  const handleClearAll = () => setSelectedIds(new Set());

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      setError("Select at least one candidate to invite.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await inviteBusinessCandidates(jobId, Array.from(selectedIds));
      onInvited(res);
      onClose();
    } catch (err) {
      setError(err?.error || "Could not send invites.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Invite candidates to interview"
      description="Inviting sends an email with a unique secure link and reserves 1 credit per candidate."
      size="md"
      footer={
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 w-full">
          <button
            type="button"
            onClick={() => {
              onClose();
              onOpenAdd();
            }}
            className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-1"
          >
            <Icon name="Plus" size={13} /> Add more candidate records
          </button>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-700 text-white hover:bg-emerald-800"
              onClick={handleSend}
              loading={busy}
              disabled={busy || selectedIds.size === 0}
              iconName="Send"
            >
              Send invites ({selectedIds.size})
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {pendingCandidates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
              <Icon name="Users" size={18} />
            </div>
            <h4 className="mt-3 text-sm font-semibold text-foreground">No pending candidates</h4>
            <p className="mt-1 text-xs text-muted-foreground">
              All added candidates have already been invited. Add more candidates to send new invites.
            </p>
            <Button
              className="mt-4 bg-emerald-700 text-white hover:bg-emerald-800"
              size="sm"
              iconName="UserPlus"
              onClick={() => {
                onClose();
                onOpenAdd();
              }}
            >
              Add candidates
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-foreground">
                {selectedIds.size} of {pendingCandidates.length} selected
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-muted-foreground hover:text-foreground font-medium"
                >
                  Select all
                </button>
                <span>·</span>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-muted-foreground hover:text-foreground font-medium"
                >
                  Clear all
                </button>
              </div>
            </div>

            <div className="max-h-64 divide-y divide-border overflow-y-auto rounded-xl border border-border">
              {pendingCandidates.map((cand) => {
                const isSel = selectedIds.has(cand.id);
                return (
                  <label
                    key={cand.id}
                    className={
                      "flex items-center gap-3 p-3 text-xs cursor-pointer hover:bg-muted/40 transition-colors " +
                      (isSel ? "bg-emerald-500/5" : "")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(cand.id)}
                      className="h-4 w-4 rounded border-input text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-foreground">{cand.name}</div>
                      <div className="text-muted-foreground">{cand.email}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </>
        )}

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <Icon name="AlertCircle" size={14} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

function topicNameOf(topic) {
  if (!topic) return "";
  if (typeof topic === "string") return topic;
  return topic.name || topic.Name || "";
}

function topicTypeOf(topic) {
  if (!topic || typeof topic === "string") return "";
  return topic.interview_type || topic.interviewType || topic.type || topic.Type || "";
}

function normalizeTopicsPayload(value) {
  const rows = Array.isArray(value?.topics) ? value.topics : Array.isArray(value) ? value : [];
  return rows
    .map((topic) => ({ name: topicNameOf(topic), type: topicTypeOf(topic) }))
    .filter((topic) => topic.name);
}

// ── Searchable multi-select dropdown for catalog topics ──────────────────────
function TopicsDropdown({ topicsList, selectedTopics, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [customTopic, setCustomTopic] = useState("");

  const filteredTopics = useMemo(() => {
    const q = search.trim().toLowerCase();
    const available = topicsList.map((t) => (typeof t === "string" ? t : t.name)).filter(Boolean);
    const set = new Set(available);
    const list = Array.from(set);
    if (!q) return list;
    return list.filter((t) => t.toLowerCase().includes(q));
  }, [topicsList, search]);

  const toggle = (t) => {
    if (selectedTopics.includes(t)) {
      onChange(selectedTopics.filter((item) => item !== t));
    } else {
      onChange([...selectedTopics, t]);
    }
  };

  const handleAddCustom = (e) => {
    e?.preventDefault();
    const val = customTopic.trim();
    if (val && !selectedTopics.includes(val)) {
      onChange([...selectedTopics, val]);
      setCustomTopic("");
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={selectedTopics.length > 0 ? "text-foreground font-medium" : "text-muted-foreground"}>
            {selectedTopics.length > 0
              ? `${selectedTopics.length} topic${selectedTopics.length === 1 ? "" : "s"} selected`
              : "Select topics from catalog…"}
          </span>
          <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
        </button>

        {open && (
          <>
            <div className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-hidden rounded-xl border border-border bg-popover p-2 shadow-xl">
              <div className="relative mb-2">
                <Icon
                  name="Search"
                  size={13}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  className="h-8 w-full rounded-lg border border-input bg-background pl-8 pr-2 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  placeholder="Search topics…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="max-h-36 overflow-y-auto space-y-0.5 pr-1">
                {filteredTopics.length === 0 ? (
                  <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                    No matching topics found
                  </div>
                ) : (
                  filteredTopics.map((topic) => {
                    const isSelected = selectedTopics.includes(topic);
                    return (
                      <button
                        key={topic}
                        type="button"
                        onClick={() => toggle(topic)}
                        className={
                          "flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors " +
                          (isSelected
                            ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold"
                            : "hover:bg-muted text-foreground")
                        }
                      >
                        <span className="truncate">{topic}</span>
                        {isSelected && <Icon name="Check" size={13} className="text-emerald-600" />}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Add custom topic */}
              <div className="mt-2 border-t border-border pt-2 flex items-center gap-1.5">
                <input
                  className="h-7 flex-1 rounded-md border border-input bg-background px-2 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  placeholder="Or type custom topic…"
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCustom();
                    }
                  }}
                />
                <Button
                  size="xs"
                  type="button"
                  variant="outline"
                  onClick={handleAddCustom}
                  disabled={!customTopic.trim()}
                >
                  Add
                </Button>
              </div>
            </div>
            <button
              type="button"
              className="fixed inset-0 z-20 cursor-default"
              onClick={() => setOpen(false)}
            />
          </>
        )}
      </div>

      {/* Selected Topics Chips */}
      {selectedTopics.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {selectedTopics.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-950/60 dark:border-emerald-800/60 dark:text-emerald-300"
            >
              <span>{t}</span>
              <button
                type="button"
                className="hover:text-destructive text-emerald-600 dark:text-emerald-400"
                onClick={() => onChange(selectedTopics.filter((item) => item !== t))}
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Question Bank Selection Modal ───────────────────────────────────────────
function RoundQuestionPicker({ open, roundType, allQuestions, selectedIds, onClose, onSave }) {
  const [localSelectedIds, setLocalSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [matchTypeOnly, setMatchTypeOnly] = useState(true);

  useEffect(() => {
    if (open) {
      setLocalSelectedIds(Array.isArray(selectedIds) ? [...selectedIds] : []);
      setSearch("");
      setMatchTypeOnly(!!roundType);
    }
  }, [open, selectedIds, roundType]);

  const toggle = (id) =>
    setLocalSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (allQuestions || []).filter((q) => {
      if (matchTypeOnly && roundType && q.type?.toLowerCase() !== roundType.toLowerCase()) return false;
      if (!s) return true;
      const title = (q?.problemStatement?.title || "").toLowerCase();
      const desc = String(q?.problemStatement?.description || "").toLowerCase();
      return title.includes(s) || desc.includes(s);
    });
  }, [allQuestions, matchTypeOnly, roundType, search]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Ask from question bank"
      description="Chosen questions are asked first, in order; prepxai's AI fills the remaining time. Leave empty for a fully AI-driven interview."
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="bg-emerald-700 text-white hover:bg-emerald-800"
            onClick={() => {
              onSave(localSelectedIds);
              onClose();
            }}
            iconName="Check"
          >
            Use {localSelectedIds.length > 0 ? `${localSelectedIds.length} ` : ""}question
            {localSelectedIds.length === 1 ? "" : "s"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Icon
              name="Search"
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500"
              placeholder="Search your question bank…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {roundType && (
            <label className="flex flex-shrink-0 items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border border-input text-emerald-600 focus:ring-emerald-500"
                checked={matchTypeOnly}
                onChange={(e) => setMatchTypeOnly(e.target.checked)}
              />
              Only {roundType} questions
            </label>
          )}
        </div>

        <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
          {visible.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-xs text-muted-foreground">
              {(allQuestions || []).length === 0
                ? "Your question bank is empty. Add or import questions from the Question Bank page first."
                : "No bank questions match your search/filter."}
            </div>
          ) : (
            visible.map((q) => {
              const idx = localSelectedIds.indexOf(q.id);
              const checked = idx !== -1;
              const title = q?.problemStatement?.title || "Untitled question";
              return (
                <label
                  key={q.id}
                  className={
                    "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors text-xs " +
                    (checked
                      ? "border-emerald-500 bg-emerald-500/5 ring-1 ring-emerald-500/20"
                      : "border-border hover:border-emerald-500/30 hover:bg-muted/30")
                  }
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-input text-emerald-600 focus:ring-emerald-500"
                    checked={checked}
                    onChange={() => toggle(q.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {checked && (
                        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">
                          {idx + 1}
                        </span>
                      )}
                      <span className="truncate font-semibold text-foreground">{title}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="rounded bg-muted px-1.5 py-0.5">{q.type}</span>
                      {q.difficulty && (
                        <span className="rounded bg-muted px-1.5 py-0.5">{q.difficulty}</span>
                      )}
                      {q.expectedTimeMinutes != null && (
                        <span>{q.expectedTimeMinutes} min</span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── Add / Edit Round Modal ──────────────────────────────────────────────────
function RoundFormModal({ open, onClose, jobId, round, onSaved }) {
  const isEdit = !!round;
  const [name, setName] = useState("");
  const [interviewType, setInterviewType] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [difficulty, setDifficulty] = useState("Medium");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [advanceRuleType, setAdvanceRuleType] = useState("cutoff");
  const [advanceRuleValue, setAdvanceRuleValue] = useState(60);
  const [manualGate, setManualGate] = useState(false);
  const [bankQuestionIds, setBankQuestionIds] = useState([]);

  const [types, setTypes] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [bankQuestions, setBankQuestions] = useState([]);
  const [questionPickerOpen, setQuestionPickerOpen] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Load catalog options, topics, and question bank
  useEffect(() => {
    Promise.all([
      listBusinessInterviewTypes(),
      listBusinessTopics(),
      listQuestionBank().catch(() => ({ questions: [] })),
    ])
      .then(([typesRes, topicsRes, bankRes]) => {
        const tList = typesRes.interviewTypes || [];
        setTypes(tList);
        setAllTopics(normalizeTopicsPayload(topicsRes));
        setBankQuestions(bankRes.questions || []);

        if (!interviewType && tList.length > 0 && !round) {
          setInterviewType(tList[0].name);
        }
      })
      .catch((e) => console.warn("Could not load form dependencies", e));
  }, []);

  // Filter topics for the current interview type
  const availableTopicsForType = useMemo(() => {
    if (!interviewType) return allTopics;
    const itLower = interviewType.toLowerCase();
    const matched = allTopics.filter(
      (t) => t.type && t.type.toLowerCase() === itLower
    );
    return matched.length > 0 ? matched : allTopics;
  }, [allTopics, interviewType]);

  useEffect(() => {
    if (round) {
      setName(round.name || "");
      setInterviewType(round.interviewType || "");
      setDurationMinutes(round.durationMinutes || 30);
      setDifficulty(round.difficulty || "Medium");
      setSelectedTopics(Array.isArray(round.topics) ? round.topics : []);
      setInstructions(round.instructions || "");
      setAdvanceRuleType(round.advanceRuleType || "cutoff");
      setAdvanceRuleValue(round.advanceRuleValue ?? 60);
      setManualGate(!!round.manualGate);
      setBankQuestionIds(Array.isArray(round.questionIds) ? round.questionIds : []);
    } else {
      setName("");
      setDurationMinutes(30);
      setDifficulty("Medium");
      setSelectedTopics([]);
      setInstructions("");
      setAdvanceRuleType("cutoff");
      setAdvanceRuleValue(60);
      setManualGate(false);
      setBankQuestionIds([]);
      if (types.length > 0) setInterviewType(types[0].name);
    }
    setError("");
  }, [round, open, types]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!interviewType) {
      setError("Please select an interview type.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: name.trim() || undefined,
        interviewType,
        durationMinutes: Number(durationMinutes) || 30,
        difficulty,
        topics: selectedTopics,
        instructions: instructions.trim() || undefined,
        advanceRuleType,
        advanceRuleValue: Number(advanceRuleValue) || 0,
        manualGate,
        questionIds: bankQuestionIds,
      };

      let res;
      if (isEdit) {
        res = await updateBusinessJobRound(jobId, round.id, payload);
      } else {
        res = await createBusinessJobRound(jobId, payload);
      }
      onSaved(res);
      onClose();
    } catch (err) {
      setError(err?.error || "Could not save the round.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={busy ? () => {} : onClose}
        title={isEdit ? `Edit Round: ${round?.name || ""}` : "Add new round"}
        description={
          isEdit
            ? "Modify the round format, interview type, topics, and advancement criteria."
            : "Define a new AI interview round in your hiring pipeline."
        }
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-700 text-white hover:bg-emerald-800"
              onClick={handleSubmit}
              loading={busy}
              disabled={busy}
            >
              {isEdit ? "Save round changes" : "Add round"}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-foreground">Round name</label>
              <Input
                className="mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Coding Interview"
                disabled={busy}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground">Interview type</label>
              <select
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={interviewType}
                onChange={(e) => setInterviewType(e.target.value)}
                disabled={busy}
              >
                {types.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-foreground">Duration</label>
              <select
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
                disabled={busy}
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
                <option value={90}>90 minutes</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground">Difficulty</label>
              <select
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                disabled={busy}
              >
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
          </div>

          {/* Topics Dropdown (Fetched from backend) */}
          <div>
            <label className="text-xs font-semibold text-foreground">Topics / Focus areas</label>
            <div className="mt-1">
              <TopicsDropdown
                topicsList={availableTopicsForType}
                selectedTopics={selectedTopics}
                onChange={setSelectedTopics}
                disabled={busy}
              />
            </div>
          </div>

          {/* Ask from question bank */}
          <div className="rounded-xl border border-border/80 bg-muted/20 p-3.5 space-y-2.5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <div className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Icon name="Database" size={14} className="text-emerald-600" />
                  Ask from question bank
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Selected questions are asked first, in order. The AI interviewer covers remaining time.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="xs"
                iconName="ListPlus"
                className="flex-shrink-0"
                onClick={() => setQuestionPickerOpen(true)}
                disabled={busy}
              >
                {bankQuestionIds.length > 0
                  ? `Select questions (${bankQuestionIds.length})`
                  : "Choose questions"}
              </Button>
            </div>

            {bankQuestionIds.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {bankQuestionIds.map((qid, idx) => {
                  const q = bankQuestions.find((item) => item.id === qid);
                  const title = q?.problemStatement?.title || "Bank question";
                  return (
                    <div
                      key={qid}
                      className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">
                          {idx + 1}
                        </span>
                        <span className="truncate font-medium text-foreground">{title}</span>
                        {q?.difficulty && (
                          <span className="rounded bg-muted px-1.5 py-0.2 text-[10px] text-muted-foreground">
                            {q.difficulty}
                          </span>
                        )}
                        {q?.expectedTimeMinutes != null && (
                          <span className="text-[10px] text-muted-foreground">
                            {q.expectedTimeMinutes}m
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-destructive p-0.5 rounded"
                        onClick={() =>
                          setBankQuestionIds((prev) => prev.filter((id) => id !== qid))
                        }
                        title="Remove question"
                      >
                        <Icon name="X" size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-foreground">Advance rule</label>
              <select
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={advanceRuleType}
                onChange={(e) => setAdvanceRuleType(e.target.value)}
                disabled={busy}
              >
                <option value="cutoff">Score cutoff (≥)</option>
                <option value="top_n">Top N candidates</option>
                <option value="top_percent">Top % percentage</option>
                <option value="none">Advance everyone</option>
              </select>
            </div>
            {advanceRuleType !== "none" && (
              <div>
                <label className="text-xs font-semibold text-foreground">
                  {advanceRuleType === "cutoff"
                    ? "Minimum score (0-100)"
                    : advanceRuleType === "top_percent"
                    ? "Percentage (%)"
                    : "Number of candidates"}
                </label>
                <Input
                  type="number"
                  className="mt-1"
                  value={advanceRuleValue}
                  onChange={(e) => setAdvanceRuleValue(e.target.value)}
                  disabled={busy}
                />
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-foreground">AI Instructions (optional)</label>
            <textarea
              className="mt-1 flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Specific instructions or custom question emphasis for the AI interviewer..."
              disabled={busy}
            />
          </div>

          <label className="flex items-center gap-2.5 rounded-xl border border-border p-3 cursor-pointer text-xs">
            <input
              type="checkbox"
              checked={manualGate}
              onChange={(e) => setManualGate(e.target.checked)}
              className="h-4 w-4 rounded border-input text-emerald-600 focus:ring-emerald-500"
              disabled={busy}
            />
            <div>
              <div className="font-semibold text-foreground">Recruiter manual review gate</div>
              <div className="text-muted-foreground">
                Pause pipeline at this round to manually approve candidates before advancing.
              </div>
            </div>
          </label>

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <Icon name="AlertCircle" size={14} />
              <span>{error}</span>
            </div>
          )}
        </form>
      </Modal>

      {/* Round Question Picker Modal */}
      <RoundQuestionPicker
        open={questionPickerOpen}
        roundType={interviewType}
        allQuestions={bankQuestions}
        selectedIds={bankQuestionIds}
        onClose={() => setQuestionPickerOpen(false)}
        onSave={(ids) => setBankQuestionIds(ids)}
      />
    </>
  );
}

// ── Round card (read-only config & settings) ─────────────────────────────────
function RoundCard({
  round,
  isPipeline,
  isFinal,
  canWrite,
  jobActive,
  isDraft,
  onReview,
  onAdvance,
  onEditRound,
  onDeleteRound,
}) {
  const ruleLabel =
    isPipeline && !isFinal
      ? (ADVANCE_RULE_LABEL[round.advanceRuleType] || ADVANCE_RULE_LABEL.none)(round.advanceRuleValue)
      : null;
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600/10 text-xs font-bold text-emerald-700 dark:text-emerald-400">
            {round.roundNumber}
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-foreground">{round.name}</span>
              <Badge className={ROUND_STATUS_BADGE[round.status]}>{titleCase(round.status)}</Badge>
              {isPipeline && isFinal && (
                <Badge className="bg-muted text-muted-foreground">Final</Badge>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Icon name="Layers" size={12} /> {round.interviewType}
              </span>
              <span className="inline-flex items-center gap-1">
                <Icon name="Clock" size={12} /> {round.durationMinutes} min
              </span>
              <span className="inline-flex items-center gap-1">
                <Icon name="Gauge" size={12} /> {round.difficulty}
              </span>
              {round.scheduledDate && (
                <span className="inline-flex items-center gap-1">
                  <Icon name="Calendar" size={12} /> {fmtDate(round.scheduledDate)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 text-right text-xs text-muted-foreground">
          <div className="font-semibold tabular-nums text-foreground">
            {round.completedCount ?? 0}/{round.assignedCount ?? 0}
          </div>
          <div>completed</div>
        </div>
      </div>

      {isDraft && canWrite && (
        <div className="mt-3 flex items-center justify-end gap-2 border-t border-border/60 pt-3">
          <Button
            size="xs"
            variant="outline"
            iconName="Edit3"
            onClick={() => onEditRound?.(round)}
          >
            Edit round
          </Button>
          {onDeleteRound && (
            <Button
              size="xs"
              variant="ghost"
              iconName="Trash2"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => onDeleteRound?.(round)}
            >
              Delete
            </Button>
          )}
        </div>
      )}

      {round.status === "awaiting_review" && (
        <div className="mt-3 flex flex-col gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-warning">
            <Icon name="PauseCircle" size={14} />
            Paused for your review — confirm who advances.
          </span>
          {canWrite && (
            <Button
              size="xs"
              variant="warning"
              iconName="ClipboardCheck"
              onClick={() => onReview(round)}
            >
              Review &amp; advance
            </Button>
          )}
        </div>
      )}

      {isPipeline && round.status === "open" && canWrite && jobActive && (
        <div className="mt-3 flex flex-col gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="CalendarClock" size={14} />
            Advances automatically on its scheduled date — or run it now.
          </span>
          <Button
            size="xs"
            variant="outline"
            iconName="FastForward"
            onClick={() => onAdvance(round)}
          >
            Advance now
          </Button>
        </div>
      )}

      {(round.topics?.length > 0 || ruleLabel) && (
        <div className="mt-3 space-y-2 border-t border-border pt-3">
          {round.topics?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {round.topics.map((t, i) => (
                <span key={i} className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  {t}
                </span>
              ))}
            </div>
          )}
          {ruleLabel && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon name="Filter" size={12} className="text-primary" />
              {ruleLabel}
              {round.manualGate && <span className="text-warning">· recruiter review</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Candidate row ────────────────────────────────────────────────────────────
function CandidateRow({ cand, canWrite, jobActive, busyId, onView, onInvite, onResend, onWithdraw }) {
  const isPending = cand.inviteStatus === "pending";
  const isBusy = busyId === cand.id;
  return (
    <li
      className="flex flex-col gap-3 border-b border-border px-4 py-3 last:border-0 hover:bg-muted/30 sm:flex-row sm:items-center sm:justify-between"
    >
      <button onClick={() => onView(cand)} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{cand.name}</span>
          <Badge className={INVITE_STATUS_BADGE[cand.inviteStatus]}>
            {titleCase(cand.inviteStatus)}
          </Badge>
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">{cand.email}</div>
      </button>

      <div className="flex items-center gap-4 sm:justify-end">
        <div className="min-w-[92px] text-xs text-muted-foreground">
          {cand.latestRound ? (
            <span className="inline-flex items-center gap-1.5">
              <span>Round {cand.latestRound}</span>
              {cand.latestRoundStatus && (
                <Badge className={ROUND_ASSIGN_BADGE[cand.latestRoundStatus]}>
                  {titleCase(cand.latestRoundStatus)}
                </Badge>
              )}
            </span>
          ) : (
            <span className="opacity-60">Not started</span>
          )}
        </div>
        <div className="w-9 text-right text-sm font-semibold tabular-nums text-foreground">
          {cand.latestScore != null ? cand.latestScore : <span className="text-muted-foreground">—</span>}
        </div>

        {canWrite && (
          <div className="flex items-center gap-1">
            {jobActive && isPending && (
              <Button
                size="xs"
                variant="outline"
                iconName="Send"
                loading={isBusy}
                disabled={isBusy}
                onClick={() => onInvite(cand)}
              >
                Invite
              </Button>
            )}
            {!isPending && (
              <Button
                size="xs"
                variant="ghost"
                iconName="RefreshCw"
                loading={isBusy}
                disabled={isBusy}
                onClick={() => onResend(cand)}
              >
                Resend
              </Button>
            )}
            <button
              onClick={() => onWithdraw(cand)}
              disabled={isBusy}
              title="Withdraw candidate"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
            >
              <Icon name="Trash2" size={15} />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

// ── Pipeline Graph Node Flow (Canvas View) ──────────────────────────────────
function PipelineGraph({
  job,
  analytics,
  selectedRoundId,
  onSelectRound,
  onAddRound,
  onEditRound,
  onDeleteRound,
  canWrite,
  onReview,
  onAdvance,
}) {
  const [zoom, setZoom] = useState(100);
  const [isLocked, setIsLocked] = useState(false);

  const isDraft = job?.status === "draft";
  const rounds = job?.rounds || [];
  const analyticsMap = useMemo(() => {
    const map = {};
    for (const r of analytics?.rounds || []) {
      if (r.roundId) map[r.roundId] = r;
      if (r.roundNumber) map[r.roundNumber] = r;
    }
    return map;
  }, [analytics]);

  const handleZoomIn = () => setZoom((z) => Math.min(150, z + 10));
  const handleZoomOut = () => setZoom((z) => Math.max(60, z - 10));
  const handleResetZoom = () => setZoom(100);

  return (
    <div className="relative flex min-h-[640px] flex-1 flex-col overflow-hidden rounded-2xl border border-border/80 bg-slate-50/70 dark:bg-card/40 p-6 sm:p-8">
      {/* Canvas background dot grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40 dark:opacity-20"
        style={{
          backgroundImage: "radial-gradient(circle, #94a3b8 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Canvas Controls Toolbar */}
      <div className="relative z-10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 rounded-xl border border-border/70 bg-card/90 p-1 shadow-xs backdrop-blur-xs">
          <button
            type="button"
            onClick={handleZoomOut}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Zoom out"
          >
            <Icon name="Minus" size={14} />
          </button>
          <button
            type="button"
            onClick={handleResetZoom}
            className="px-2 text-xs font-semibold tabular-nums text-foreground hover:text-primary"
            title="Reset zoom"
          >
            {zoom}%
          </button>
          <button
            type="button"
            onClick={handleZoomIn}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Zoom in"
          >
            <Icon name="Plus" size={14} />
          </button>
          <div className="h-4 w-px bg-border mx-0.5" />
          <button
            type="button"
            onClick={handleResetZoom}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Fit to view"
          >
            <Icon name="Maximize2" size={13} />
          </button>
          <button
            type="button"
            onClick={() => setIsLocked(!isLocked)}
            className={
              "flex h-7 w-7 items-center justify-center rounded-lg transition-colors " +
              (isLocked
                ? "bg-primary/10 text-primary font-medium"
                : "text-muted-foreground hover:bg-muted hover:text-foreground")
            }
            title={isLocked ? "Unlock canvas" : "Lock canvas"}
          >
            <Icon name={isLocked ? "Lock" : "Unlock"} size={13} />
          </button>
        </div>

        {canWrite && isDraft && (
          <button
            type="button"
            onClick={onAddRound}
            className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-600/40 bg-card px-3.5 py-1.5 text-xs font-semibold text-emerald-700 shadow-xs hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
          >
            <Icon name="Plus" size={14} />
            <span>Add round</span>
          </button>
        )}
      </div>

      {/* Nodes Flow Area */}
      <div className="relative z-10 my-auto flex flex-1 flex-col items-center justify-center py-6">
        <div
          className="flex flex-col items-center transition-transform duration-200"
          style={{ transform: `scale(${zoom / 100})`, transformOrigin: "top center" }}
        >
          {rounds.map((round, idx) => {
            const isSelected = selectedRoundId === round.id;
            const isLast = idx === rounds.length - 1;
            const rAnal = analyticsMap[round.id] || analyticsMap[round.roundNumber];
            const invited = round.assignedCount ?? (rAnal?.total ?? 0);
            const completed = round.completedCount ?? (rAnal?.completed ?? 0);
            const passed = rAnal?.passed ?? 0;
            const iconName = getRoundIconName(round.interviewType || round.name);
            const advanceRuleText = getAdvanceBadgeLabel(round);

            return (
              <React.Fragment key={round.id || idx}>
                {/* Round Node Card */}
                <div
                  onClick={() => onSelectRound(round)}
                  className={
                    "w-[340px] sm:w-[380px] rounded-2xl p-4 transition-all cursor-pointer " +
                    (isSelected
                      ? "border-2 border-emerald-500 bg-card shadow-md ring-4 ring-emerald-500/10"
                      : "border border-border/90 bg-card shadow-xs hover:border-emerald-500/50 hover:shadow-md")
                  }
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-bold text-white">
                        {round.roundNumber}
                      </span>
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                        <Icon name={iconName} size={16} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="truncate text-sm font-semibold text-foreground">
                          {round.name}
                        </h4>
                        <p className="truncate text-xs text-muted-foreground">
                          {round.durationMinutes} min · {round.difficulty}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isDraft && canWrite ? (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="p-1 text-muted-foreground hover:text-foreground rounded"
                            title="Edit round"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditRound?.(round);
                            }}
                          >
                            <Icon name="Edit3" size={14} />
                          </button>
                          {rounds.length > 1 && (
                            <button
                              type="button"
                              className="p-1 text-muted-foreground hover:text-destructive rounded"
                              title="Delete round"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteRound?.(round);
                              }}
                            >
                              <Icon name="Trash2" size={14} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="p-1 text-muted-foreground hover:text-foreground rounded"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectRound(round);
                          }}
                        >
                          <Icon name="MoreHorizontal" size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Card Stats Grid */}
                  <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-muted/40 p-2.5 text-center">
                    <div>
                      <div className="text-base font-semibold tabular-nums text-foreground">
                        {invited}
                      </div>
                      <div className="text-[11px] text-muted-foreground">Invited</div>
                    </div>
                    <div className="border-x border-border/60">
                      <div className="text-base font-semibold tabular-nums text-foreground">
                        {completed}
                      </div>
                      <div className="text-[11px] text-muted-foreground">Completed</div>
                    </div>
                    <div>
                      <div className="text-base font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                        {passed}
                      </div>
                      <div className="text-[11px] text-muted-foreground">Passed</div>
                    </div>
                  </div>
                </div>

                {/* Connector with Rule Badge */}
                {!isLast && (
                  <div className="flex flex-col items-center my-1.5 relative">
                    <div className="w-0.5 h-12 bg-border relative flex items-center justify-center">
                      <span className="absolute z-10 whitespace-nowrap rounded-full border border-emerald-300 dark:border-emerald-800 bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 shadow-xs">
                        {advanceRuleText}
                      </span>
                    </div>
                    <Icon name="ChevronDown" size={14} className="-mt-1 text-muted-foreground" />
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {/* Plus action at bottom of flow */}
          {rounds.length > 0 && isDraft && canWrite && (
            <div className="flex flex-col items-center mt-2">
              <div className="w-0.5 h-6 border-l border-dashed border-border" />
              <button
                type="button"
                onClick={onAddRound}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-border bg-card text-muted-foreground hover:border-emerald-500 hover:text-emerald-600 transition-colors"
                title="Add round"
              >
                <Icon name="Plus" size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mini-map Box at bottom-left */}
      <div className="relative z-10 flex items-center justify-between">
        <div className="flex h-20 w-28 items-center justify-center rounded-xl border border-border/80 bg-card/90 p-2 shadow-xs backdrop-blur-xs">
          <div className="relative flex flex-col items-center gap-1.5 w-full">
            {rounds.map((r, i) => (
              <div
                key={r.id || i}
                className={
                  "h-2.5 w-16 rounded-xs transition-colors " +
                  (selectedRoundId === r.id ? "bg-emerald-500 ring-1 ring-emerald-500" : "bg-muted-foreground/30")
                }
              />
            ))}
            <div className="absolute -inset-1 rounded border border-emerald-500/80 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Round Detail Right-Side Slide-in Panel ──────────────────────────────────
function RoundDetailPanel({
  round,
  job,
  roundAnalytics,
  candidates,
  onClose,
  onViewCandidate,
  onViewAllCandidates,
  onEditRound,
  onDeleteRound,
  canWrite,
  jobActive,
  onReview,
  onAdvance,
}) {
  const [panelTab, setPanelTab] = useState("candidates"); // candidates | settings
  const [search, setSearch] = useState("");

  const isDraft = job?.status === "draft";
  const iconName = getRoundIconName(round?.interviewType || round?.name);

  // Statistics
  const invited = round?.assignedCount ?? (roundAnalytics?.total ?? 0);
  const completed = round?.completedCount ?? (roundAnalytics?.completed ?? 0);
  const passed = roundAnalytics?.passed ?? 0;
  const passRate = completed > 0 ? ((passed / completed) * 100).toFixed(1) : "0.0";
  const avgScore = roundAnalytics?.avgScore != null ? Math.round(roundAnalytics.avgScore) : "—";

  // Candidates list filtered for this search
  const displayCandidates = useMemo(() => {
    const list = (candidates || [])
      .map((candidate) => ({
        ...candidate,
        selectedRoundAssignment: getCandidateRoundAssignment(candidate, round),
      }))
      .filter((candidate) => !!candidate.selectedRoundAssignment);
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(
      (c) =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q))
    );
  }, [candidates, round, search]);

  if (!round) return null;

  return (
    <div className="flex w-full flex-col rounded-2xl border border-border/90 bg-card p-5 shadow-sm lg:w-[390px] xl:w-[430px] flex-shrink-0">
      {/* Top Header */}
      <div className="flex items-start justify-between gap-3 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
            <Icon name={iconName} size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-foreground">Round {round.roundNumber}</h3>
              {isDraft && canWrite && (
                <button
                  type="button"
                  onClick={() => onEditRound?.(round)}
                  className="text-xs text-emerald-700 hover:underline dark:text-emerald-400 font-medium"
                >
                  Edit
                </button>
              )}
            </div>
            <p className="text-xs font-medium text-foreground">{round.name}</p>
            <p className="text-xs text-muted-foreground">
              {round.durationMinutes} min · {round.difficulty}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Close panel"
        >
          <Icon name="X" size={18} />
        </button>
      </div>

      {/* 4-Stat Strip */}
      <div className="grid grid-cols-4 gap-2 py-4">
        <div className="rounded-xl border border-border/70 bg-background/50 p-2.5 text-center">
          <div className="text-base font-bold tabular-nums text-foreground">{invited}</div>
          <div className="text-[10px] text-muted-foreground">Invited</div>
        </div>
        <div className="rounded-xl border border-border/70 bg-background/50 p-2.5 text-center">
          <div className="text-base font-bold tabular-nums text-foreground">{completed}</div>
          <div className="text-[10px] text-muted-foreground">Completed</div>
        </div>
        <div className="rounded-xl border border-border/70 bg-background/50 p-2.5 text-center">
          <div className="text-base font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
            {passed}
          </div>
          <div className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
            {completed > 0 ? `${passRate}%` : "Passed"}
          </div>
        </div>
        <div className="rounded-xl border border-border/70 bg-background/50 p-2.5 text-center">
          <div className="text-base font-bold tabular-nums text-foreground">{avgScore}</div>
          <div className="text-[10px] text-muted-foreground">Avg. score</div>
        </div>
      </div>

      {/* Panel Sub-Tabs */}
      <div className="flex border-b border-border/70">
        <button
          type="button"
          onClick={() => setPanelTab("candidates")}
          className={
            "flex-1 py-2 text-center text-xs font-semibold transition-colors border-b-2 " +
            (panelTab === "candidates"
              ? "border-emerald-600 text-emerald-600 dark:border-emerald-400 dark:text-emerald-400"
              : "border-transparent text-muted-foreground hover:text-foreground")
          }
        >
          Candidates
        </button>
        <button
          type="button"
          onClick={() => setPanelTab("settings")}
          className={
            "flex-1 py-2 text-center text-xs font-semibold transition-colors border-b-2 " +
            (panelTab === "settings"
              ? "border-emerald-600 text-emerald-600 dark:border-emerald-400 dark:text-emerald-400"
              : "border-transparent text-muted-foreground hover:text-foreground")
          }
        >
          Settings
        </button>
      </div>

      {/* Sub-tab: Candidates */}
      {panelTab === "candidates" && (
        <div className="flex flex-1 flex-col justify-between space-y-4 pt-3">
          {/* Search + Filter */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Icon
                name="Search"
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                className="h-8 w-full rounded-lg border border-input bg-background pl-8 pr-3 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500"
                placeholder="Search candidates…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-input bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Filter"
            >
              <Icon name="SlidersHorizontal" size={14} />
            </button>
          </div>

          {/* Candidates Scrollable List */}
          <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
            {displayCandidates.length === 0 ? (
              <p className="py-8 text-center text-xs text-muted-foreground">
                {search ? "No matching candidates" : "No candidates invited to this round yet."}
              </p>
            ) : (
              displayCandidates.map((cand) => {
                const colorClass = getAvatarColor(cand.name);
                const initials = getInitials(cand.name);
                const roundAssignment = cand.selectedRoundAssignment || {};
                const score = roundAssignment.score;
                const status = roundAssignment.status;
                const isPassed = status === "passed" || (score != null && score >= 60);
                const isFailed = status === "failed" || (score != null && score < 50);
                const isStarted = status === "started" || status === "in_progress";
                const isCompleted = status === "completed";

                return (
                  <div
                    key={cand.id}
                    onClick={() => onViewCandidate(cand)}
                    className="group flex items-center justify-between gap-2.5 rounded-xl border border-border/60 bg-background/60 p-2.5 hover:border-emerald-500/50 hover:bg-muted/30 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className={
                          "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold " +
                          colorClass
                        }
                      >
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-xs font-semibold text-foreground">
                          {cand.name}
                        </div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {cand.email}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {roundAssignment.completedAt
                            ? `Completed ${fmtDate(roundAssignment.completedAt)}`
                            : ["invited", "started", "in_progress"].includes(status)
                            ? "Invited"
                            : "Not attempted"}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-sm font-bold tabular-nums text-foreground">
                          {score != null ? score : "—"}
                        </div>
                        <div
                          className={
                            "text-[10px] font-medium " +
                            (isPassed
                              ? "text-emerald-600 dark:text-emerald-400"
                              : isFailed
                              ? "text-destructive"
                              : "text-muted-foreground")
                          }
                        >
                          {isPassed
                            ? "Passed"
                            : isFailed
                            ? "Failed"
                            : isStarted
                            ? "In progress"
                            : isCompleted
                            ? "Completed"
                            : "Not attempted"}
                        </div>
                      </div>
                      <Icon
                        name="ChevronRight"
                        size={15}
                        className="text-muted-foreground group-hover:text-foreground"
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Bottom Button */}
          <button
            type="button"
            onClick={onViewAllCandidates}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 py-2.5 text-xs font-semibold text-white shadow-xs hover:bg-emerald-800 transition-colors"
          >
            <span>View all candidates</span>
            <Icon name="ArrowRight" size={14} />
          </button>
        </div>
      )}

      {/* Sub-tab: Settings */}
      {panelTab === "settings" && (
        <div className="mt-3 space-y-4 text-xs">
          <RoundCard
            round={round}
            isPipeline={job?.mode === "pipeline"}
            isFinal={round.roundNumber >= (job?.numRounds || 1)}
            canWrite={canWrite}
            jobActive={jobActive}
            isDraft={isDraft}
            onReview={onReview}
            onAdvance={onAdvance}
            onEditRound={onEditRound}
            onDeleteRound={(job?.rounds || []).length > 1 ? onDeleteRound : null}
          />
        </div>
      )}
    </div>
  );
}

function AnalyticsView({ job, analytics }) {
  if (!analytics) return <div className="py-10 text-center text-muted-foreground">Loading analytics...</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Total Candidates</div>
          <div className="mt-1 text-2xl font-semibold">{analytics.totalCandidates}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">In Flight</div>
          <div className="mt-1 text-2xl font-semibold">{analytics.active}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Finalists</div>
          <div className="mt-1 text-2xl font-semibold text-success">{analytics.finalists}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Hired</div>
          <div className="mt-1 text-2xl font-semibold text-primary">{analytics.hired}</div>
        </div>
      </div>
      
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">Pipeline Funnel</h3>
        {analytics.rounds?.map((r, i) => (
          <div key={r.roundId || i} className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold">{r.roundNumber}. {r.name}</div>
              <div className="text-sm text-muted-foreground">Avg Score: {r.avgScore || '-'}</div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center sm:grid-cols-6 mb-6">
              <div>
                <div className="text-xl font-medium">{r.invited}</div>
                <div className="text-xs text-muted-foreground uppercase">Invited</div>
              </div>
              <div>
                <div className="text-xl font-medium">{r.started}</div>
                <div className="text-xs text-muted-foreground uppercase">Started</div>
              </div>
              <div>
                <div className="text-xl font-medium">{r.completed}</div>
                <div className="text-xs text-muted-foreground uppercase">Completed</div>
              </div>
              <div>
                <div className="text-xl font-medium text-success">{r.passed}</div>
                <div className="text-xs text-muted-foreground uppercase">Passed</div>
              </div>
              <div>
                <div className="text-xl font-medium text-destructive">{r.failed}</div>
                <div className="text-xs text-muted-foreground uppercase">Failed</div>
              </div>
              <div>
                <div className="text-xl font-medium">{r.expired + r.cancelled}</div>
                <div className="text-xs text-muted-foreground uppercase">Dropped</div>
              </div>
            </div>
            {r.scoreBuckets && (
              <div>
                <div className="text-sm font-medium text-foreground mb-2">Score Distribution</div>
                <div className="flex h-24 items-end gap-1">
                  {r.scoreBuckets.map(b => {
                    const max = Math.max(...r.scoreBuckets.map(x => x.count), 1);
                    const h = (b.count / max) * 100;
                    return (
                      <div key={b.label} className="flex flex-1 flex-col items-center gap-1 group relative">
                        <div className="w-full bg-primary/20 hover:bg-primary transition-colors rounded-t-sm" style={{ height: `${h}%`, minHeight: b.count > 0 ? '4px' : '0' }}></div>
                        <div className="text-[10px] text-muted-foreground truncate w-full text-center">{b.label}</div>
                        <div className="absolute -top-6 hidden group-hover:block bg-foreground text-background text-xs px-2 py-1 rounded opacity-90 z-10">{b.count}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { organization } = useAuth();
  const canWrite = WRITER_ROLES.includes(organization?.role);

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [candidates, setCandidates] = useState([]);
  const [activeTab, setActiveTab] = useState("pipeline"); // 'pipeline' | 'candidates' | 'analytics'
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const [candTotal, setCandTotal] = useState(0);
  const [candLoading, setCandLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [inviteFilter, setInviteFilter] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);

  const [banner, setBanner] = useState(null); // { type, text, action? }
  const [rowBusyId, setRowBusyId] = useState("");

  // Modals / dialogs
  const [addOpen, setAddOpen] = useState(false); // Add Candidates modal
  const [editJobOpen, setEditJobOpen] = useState(false); // Edit Job modal
  const [inviteModalOpen, setInviteModalOpen] = useState(false); // Invite Candidates modal
  const [roundModal, setRoundModal] = useState(null); // { mode: 'create' | 'edit', round?: Object }
  const [detailId, setDetailId] = useState("");
  const [confirm, setConfirm] = useState(null); // { kind, cand?, round? }
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [headerBusy, setHeaderBusy] = useState("");
  const [reviewRoundId, setReviewRoundId] = useState("");
  const [advanceRound, setAdvanceRound] = useState(null); // round object pending "advance now"
  const [advanceBusy, setAdvanceBusy] = useState(false);

  const loadJob = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await getBusinessJob(jobId);
      setJob(data);
      if (data?.rounds?.length > 0) {
        setSelectedRoundId((prev) => (prev && data.rounds.some((r) => r.id === prev) ? prev : data.rounds[0].id));
      }
      // Load analytics in parallel — non-fatal if it fails.
      getBusinessJobAnalytics(jobId)
        .then(setAnalytics)
        .catch((e) => console.warn("Analytics load failed", e));
    } catch (err) {
      setLoadError(err?.error || "Could not load the job.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const loadCandidates = useCallback(async () => {
    setCandLoading(true);
    try {
      const params = { limit };
      if (search.trim()) params.search = search.trim();
      if (inviteFilter) params.inviteStatus = inviteFilter;
      const data = await listBusinessCandidates(jobId, params);
      setCandidates(data.candidates || []);
      setCandTotal(data.total || 0);
    } catch (err) {
      setBanner({ type: "error", text: err?.error || "Could not load candidates." });
    } finally {
      setCandLoading(false);
    }
  }, [jobId, limit, search, inviteFilter]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  // Debounce candidate reloads on search/filter/limit changes.
  useEffect(() => {
    const t = setTimeout(loadCandidates, 250);
    return () => clearTimeout(t);
  }, [loadCandidates]);

  const isPipeline = job?.mode === "pipeline";
  const rounds = job?.rounds || [];
  const selectedRound = useMemo(
    () => rounds.find((r) => r.id === selectedRoundId) || rounds[0] || null,
    [rounds, selectedRoundId]
  );
  const selectedRoundAnalytics = useMemo(() => {
    if (!selectedRound || !analytics?.rounds) return null;
    return (
      analytics.rounds.find(
        (r) => r.roundId === selectedRound.id || r.roundNumber === selectedRound.roundNumber
      ) || null
    );
  }, [selectedRound, analytics]);

  const pendingCount = useMemo(
    () => candidates.filter((c) => c.inviteStatus === "pending").length,
    [candidates]
  );
  const awaitingRounds = useMemo(
    () => rounds.filter((r) => r.status === "awaiting_review"),
    [rounds]
  );

  // ── Candidate actions ──────────────────────────────────────────────────────
  const refreshAll = async () => {
    await Promise.all([loadJob(), loadCandidates()]);
  };

  const handleInviteOne = async (cand) => {
    setRowBusyId(cand.id);
    setBanner(null);
    try {
      const res = await inviteBusinessCandidates(jobId, [cand.id]);
      if (res.invited > 0) {
        setBanner({ type: "success", text: `Invited ${cand.name}. A credit was reserved.` });
      } else if (res.walletExhausted) {
        setBanner({
          type: "warning",
          text: "Not enough credits to invite. Buy more to continue.",
          action: { label: "Buy credits", to: "/business/buy-credits" },
        });
      } else {
        setBanner({ type: "warning", text: "No invite was sent (already invited?)." });
      }
      await refreshAll();
    } catch (err) {
      const b = { type: "error", text: err?.error || "Could not send the invite." };
      if (err?.walletExhausted) b.action = { label: "Buy credits", to: "/business/buy-credits" };
      setBanner(b);
    } finally {
      setRowBusyId("");
    }
  };

  const handleInviteAll = async () => {
    setHeaderBusy("invite");
    setBanner(null);
    try {
      const res = await inviteBusinessCandidates(jobId, []);
      const parts = [];
      if (res.invited > 0) parts.push(`${res.invited} invited`);
      if (res.skipped > 0) parts.push(`${res.skipped} skipped`);
      if (res.failed > 0) parts.push(`${res.failed} failed`);
      if (res.emailFailed > 0) parts.push(`${res.emailFailed} email(s) failed`);
      const text = parts.length ? parts.join(" · ") : "No eligible candidates to invite.";
      const b = { type: res.invited > 0 ? "success" : "warning", text };
      if (res.walletExhausted)
        b.action = { label: "Buy credits", to: "/business/buy-credits" };
      setBanner(b);
      await refreshAll();
    } catch (err) {
      const b = { type: "error", text: err?.error || "Could not send invites." };
      if (err?.walletExhausted) b.action = { label: "Buy credits", to: "/business/buy-credits" };
      setBanner(b);
    } finally {
      setHeaderBusy("");
    }
  };

  const handleResend = async (cand) => {
    setRowBusyId(cand.id);
    setBanner(null);
    try {
      await resendBusinessCandidateInvite(jobId, cand.id);
      setBanner({ type: "success", text: `Invite re-sent to ${cand.name}.` });
    } catch (err) {
      setBanner({ type: "error", text: err?.error || "Could not resend the invite." });
    } finally {
      setRowBusyId("");
    }
  };

  const doWithdraw = async () => {
    const cand = confirm?.cand;
    if (!cand) return;
    setConfirmBusy(true);
    try {
      const res = await withdrawBusinessCandidate(jobId, cand.id);
      const released = res?.creditsReleased || 0;
      setBanner({
        type: "success",
        text:
          `Withdrew ${cand.name}.` +
          (released > 0 ? ` ${released} credit${released === 1 ? "" : "s"} released.` : ""),
      });
      setConfirm(null);
      await refreshAll();
    } catch (err) {
      setBanner({ type: "error", text: err?.error || "Could not withdraw the candidate." });
    } finally {
      setConfirmBusy(false);
    }
  };

  // ── Round Delete Action ───────────────────────────────────────────────────
  const doDeleteRound = async () => {
    const round = confirm?.round;
    if (!round) return;
    setConfirmBusy(true);
    try {
      const updatedJob = await deleteBusinessJobRound(jobId, round.id);
      setJob(updatedJob);
      setConfirm(null);
      setBanner({ type: "success", text: `Deleted ${round.name}.` });
      if (selectedRoundId === round.id && updatedJob.rounds?.length > 0) {
        setSelectedRoundId(updatedJob.rounds[0].id);
      }
    } catch (err) {
      setBanner({ type: "error", text: err?.error || "Could not delete the round." });
    } finally {
      setConfirmBusy(false);
    }
  };

  // ── Round review (manual gate) ───────────────────────────────────────────────
  const handleReviewConfirmed = async (result, err) => {
    setReviewRoundId("");
    if (err?.conflict) {
      setBanner({
        type: "warning",
        text: "That round was already resolved. The timeline has been refreshed.",
      });
      await loadJob();
      return;
    }
    if (!result) return;
    const parts = [];
    if (result.nextRoundOpen) {
      if (result.invited > 0)
        parts.push(`${result.invited} advanced to round ${result.roundNumber + 1}`);
      if (result.skippedWallet > 0)
        parts.push(`${result.skippedWallet} not invited (out of credits)`);
    } else if (result.finalists > 0) {
      parts.push(`${result.finalists} finalist${result.finalists === 1 ? "" : "s"} selected`);
    }
    if (result.failed > 0) parts.push(`${result.failed} not advanced`);
    const text =
      `Round ${result.roundNumber} confirmed` + (parts.length ? ` — ${parts.join(" · ")}.` : ".");
    const b = { type: "success", text };
    if (result.skippedWallet > 0) b.action = { label: "Buy credits", to: "/business/buy-credits" };
    setBanner(b);
    await refreshAll();
  };

  // Run a round's advancement boundary immediately (recruiter's "Advance now").
  const handleAdvanceNow = async () => {
    if (!advanceRound) return;
    const round = advanceRound;
    setAdvanceBusy(true);
    setBanner(null);
    try {
      const result = await advanceBusinessRound(jobId, round.id);
      setAdvanceRound(null);
      if (result.paused) {
        setBanner({
          type: "warning",
          text: `Round ${result.roundNumber} is now awaiting your review — confirm who advances.`,
        });
        await refreshAll();
        setReviewRoundId(round.id);
        return;
      }
      const parts = [];
      if (result.noShows > 0) parts.push(`${result.noShows} no-show${result.noShows === 1 ? "" : "s"} expired`);
      if (result.nextRoundOpen) {
        if (result.invited > 0)
          parts.push(`${result.invited} advanced to round ${result.roundNumber + 1}`);
        if (result.skippedWallet > 0) parts.push(`${result.skippedWallet} not invited (out of credits)`);
      } else if (result.finalists > 0) {
        parts.push(`${result.finalists} finalist${result.finalists === 1 ? "" : "s"} selected`);
      }
      if (result.failed > 0) parts.push(`${result.failed} not advanced`);
      const b = {
        type: "success",
        text: `Round ${result.roundNumber} advanced` + (parts.length ? ` — ${parts.join(" · ")}.` : "."),
      };
      if (result.skippedWallet > 0) b.action = { label: "Buy credits", to: "/business/buy-credits" };
      setBanner(b);
      await refreshAll();
    } catch (err) {
      setAdvanceRound(null);
      if (err?.conflict) {
        setBanner({
          type: "warning",
          text: "That round could not be advanced (it may have already advanced). The timeline has been refreshed.",
        });
        await loadJob();
        return;
      }
      setBanner({ type: "error", text: err?.error || "Could not advance the round." });
    } finally {
      setAdvanceBusy(false);
    }
  };

  // ── Lifecycle actions ──────────────────────────────────────────────────────
  const doActivate = async () => {
    setHeaderBusy("activate");
    setBanner(null);
    try {
      await activateBusinessJob(jobId);
      setBanner({ type: "success", text: "Job activated. Round 1 is open — invite candidates to begin." });
      await loadJob();
    } catch (err) {
      setBanner({ type: "error", text: err?.error || "Could not activate the job." });
    } finally {
      setHeaderBusy("");
    }
  };

  const doClose = async () => {
    setConfirmBusy(true);
    try {
      await closeBusinessJob(jobId);
      setBanner({ type: "success", text: "Job closed. No new interviews will be accepted." });
      setConfirm(null);
      await loadJob();
    } catch (err) {
      setBanner({ type: "error", text: err?.error || "Could not close the job." });
    } finally {
      setConfirmBusy(false);
    }
  };

  const doDelete = async () => {
    setConfirmBusy(true);
    try {
      await deleteBusinessJob(jobId);
      navigate("/business/jobs", { replace: true });
    } catch (err) {
      setBanner({ type: "error", text: err?.error || "Could not delete the job." });
      setConfirmBusy(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="h-8 w-64 animate-pulse rounded bg-muted/50" />
        <div className="h-40 animate-pulse rounded-xl bg-muted/50" />
        <div className="h-64 animate-pulse rounded-xl bg-muted/50" />
      </div>
    );
  }

  if (loadError || !job) {
    return (
      <div className="mx-auto max-w-7xl">
        <button
          onClick={() => navigate("/business/jobs")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <Icon name="ArrowLeft" size={15} /> Back to jobs
        </button>
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{loadError || "Job not found."}</span>
          <button onClick={loadJob} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const bannerStyles = {
    success: "border-success/30 bg-success/10 text-success",
    error: "border-destructive/30 bg-destructive/10 text-destructive",
    warning: "border-warning/30 bg-warning/10 text-warning",
  };
  const bannerIcon = { success: "CheckCircle2", error: "AlertCircle", warning: "AlertTriangle" };

  const isDraft = job.status === "draft";
  const isActive = job.status === "active";

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      {/* Header with Back button and Actions */}
      <div>
        <button
          onClick={() => navigate("/business/jobs")}
          className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground mb-1.5"
        >
          <Icon name="ArrowLeft" size={13} /> Jobs
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-foreground">{job.title}</h1>
              <span
                className={
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize " +
                  (isActive
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
                    : JOB_STATUS_BADGE[job.status] || "bg-muted text-muted-foreground")
                }
              >
                {job.status}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
              <span>
                {isPipeline ? `${job.numRounds || rounds.length}-round pipeline` : "Single interview"}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Icon name="Calendar" size={12} />
                Due {fmtDate(job.deadlineAt) || "No due date"}
              </span>
              {job.department && (
                <>
                  <span>·</span>
                  <span>{job.department}</span>
                </>
              )}
              {job.location && (
                <>
                  <span>·</span>
                  <span>{job.location}</span>
                </>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2.5">
            {canWrite && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit3"
                  className="rounded-xl border-border/80 text-foreground"
                  onClick={() => setEditJobOpen(true)}
                >
                  Edit job
                </Button>
                <Button
                  size="sm"
                  iconName="UserPlus"
                  className="rounded-xl bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => setInviteModalOpen(true)}
                  loading={headerBusy === "invite"}
                >
                  Invite candidates
                </Button>
              </>
            )}

            {/* Overflow Options Menu */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/80 bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Icon name="MoreVertical" size={16} />
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-48 rounded-xl border border-border bg-card p-1.5 shadow-lg z-30"
                  onBlur={() => setTimeout(() => setMenuOpen(false), 200)}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setAddOpen(true);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-foreground hover:bg-muted"
                  >
                    <Icon name="UserPlus" size={14} />
                    Add candidates
                  </button>
                  {isDraft && canWrite && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setRoundModal({ mode: "create" });
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-foreground hover:bg-muted"
                    >
                      <Icon name="Plus" size={14} className="text-emerald-600" />
                      Add round
                    </button>
                  )}
                  {isDraft && canWrite && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        doActivate();
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-foreground hover:bg-muted"
                    >
                      <Icon name="Rocket" size={14} className="text-emerald-600" />
                      Activate job
                    </button>
                  )}
                  {isActive && canWrite && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirm({ kind: "close" });
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-warning hover:bg-muted"
                    >
                      <Icon name="Ban" size={14} />
                      Close job
                    </button>
                  )}
                  {isDraft && canWrite && job.candidateCount === 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirm({ kind: "delete" });
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-destructive hover:bg-muted"
                    >
                      <Icon name="Trash2" size={14} />
                      Delete draft
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Banner */}
      {banner && (
        <div
          className={
            "flex items-center gap-2 rounded-xl border px-4 py-3 text-sm " + bannerStyles[banner.type]
          }
        >
          <Icon name={bannerIcon[banner.type]} size={16} className="flex-shrink-0" />
          <span>{banner.text}</span>
          {banner.action && (
            <button
              onClick={() => navigate(banner.action.to)}
              className="ml-1 font-semibold underline"
            >
              {banner.action.label}
            </button>
          )}
          <button onClick={() => setBanner(null)} className="ml-auto opacity-60 hover:opacity-100">
            <Icon name="X" size={15} />
          </button>
        </div>
      )}

      {/* Awaiting Review Notice */}
      {canWrite && awaitingRounds.length > 0 && (
        <div className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2 text-sm text-warning">
            <Icon name="PauseCircle" size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              {awaitingRounds.length === 1
                ? `${awaitingRounds[0].name} is paused for your review — confirm who advances to continue the pipeline.`
                : `${awaitingRounds.length} rounds are paused for your review.`}
            </span>
          </div>
          <Button
            size="sm"
            variant="warning"
            iconName="ClipboardCheck"
            className="flex-shrink-0"
            onClick={() => setReviewRoundId(awaitingRounds[0].id)}
          >
            Review {awaitingRounds[0].name}
          </Button>
        </div>
      )}

      {/* Top Tabs */}
      <div className="flex items-center gap-6 border-b border-border/70 text-sm font-semibold">
        <button
          type="button"
          onClick={() => setActiveTab("pipeline")}
          className={
            "-mb-px flex items-center gap-2 border-b-2 pb-3 font-semibold transition-colors " +
            (activeTab === "pipeline"
              ? "border-emerald-600 text-emerald-600 dark:border-emerald-400 dark:text-emerald-400"
              : "border-transparent text-muted-foreground hover:text-foreground")
          }
        >
          Pipeline
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("candidates")}
          className={
            "-mb-px flex items-center gap-2 border-b-2 pb-3 font-semibold transition-colors " +
            (activeTab === "candidates"
              ? "border-emerald-600 text-emerald-600 dark:border-emerald-400 dark:text-emerald-400"
              : "border-transparent text-muted-foreground hover:text-foreground")
          }
        >
          <span>Candidates</span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground font-medium">
            {candTotal || candidates.length || job.candidateCount || 0}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("analytics")}
          className={
            "-mb-px flex items-center gap-2 border-b-2 pb-3 font-semibold transition-colors " +
            (activeTab === "analytics"
              ? "border-emerald-600 text-emerald-600 dark:border-emerald-400 dark:text-emerald-400"
              : "border-transparent text-muted-foreground hover:text-foreground")
          }
        >
          Analytics
        </button>
      </div>

      {/* Active Tab View */}
      {activeTab === "pipeline" && (
        <div className="flex flex-col lg:flex-row items-start gap-6">
          {/* Pipeline Canvas Area */}
          <PipelineGraph
            job={job}
            analytics={analytics}
            selectedRoundId={selectedRoundId}
            onSelectRound={(r) => setSelectedRoundId(r.id)}
            onAddRound={() => setRoundModal({ mode: "create" })}
            onEditRound={(r) => setRoundModal({ mode: "edit", round: r })}
            onDeleteRound={(r) => setConfirm({ kind: "delete_round", round: r })}
            canWrite={canWrite}
            onReview={(r) => setReviewRoundId(r.id)}
            onAdvance={(r) => setAdvanceRound(r)}
          />

          {/* Right Slide-in Detail Panel */}
          {selectedRound && (
            <RoundDetailPanel
              round={selectedRound}
              job={job}
              roundAnalytics={selectedRoundAnalytics}
              candidates={candidates}
              onClose={() => setSelectedRoundId(null)}
              onViewCandidate={(c) => setDetailId(c.id)}
              onViewAllCandidates={() => setActiveTab("candidates")}
              onEditRound={(r) => setRoundModal({ mode: "edit", round: r })}
              onDeleteRound={(r) => setConfirm({ kind: "delete_round", round: r })}
              canWrite={canWrite}
              jobActive={isActive}
              onReview={(r) => setReviewRoundId(r.id)}
              onAdvance={(r) => setAdvanceRound(r)}
            />
          )}
        </div>
      )}

      {activeTab === "candidates" && (
        <section className="rounded-2xl border border-border/80 bg-card shadow-xs">
          <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <Icon name="Users" size={18} className="text-emerald-600" />
                Candidates Funnel
              </h2>
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                {candTotal || candidates.length}
              </span>
            </div>
            {canWrite && (
              <div className="flex items-center gap-2">
                {isActive && pendingCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Send"
                    loading={headerBusy === "invite"}
                    disabled={!!headerBusy}
                    onClick={() => setInviteModalOpen(true)}
                  >
                    Invite all pending ({pendingCount})
                  </Button>
                )}
                <Button
                  size="sm"
                  iconName="UserPlus"
                  className="bg-emerald-700 text-white hover:bg-emerald-800"
                  disabled={job.status === "closed" || job.status === "archived"}
                  onClick={() => setAddOpen(true)}
                >
                  Add candidates
                </Button>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Icon
                name="Search"
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500"
                placeholder="Search name, email, or phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-1.5">
              {[
                { key: "", label: "All" },
                { key: "pending", label: "Not invited" },
                { key: "invited", label: "Invited" },
              ].map((f) => (
                <button
                  key={f.key || "all"}
                  onClick={() => setInviteFilter(f.key)}
                  className={
                    "rounded-full px-3 py-1 text-xs font-semibold transition-colors " +
                    (inviteFilter === f.key
                      ? "bg-emerald-700 text-white"
                      : "bg-muted text-muted-foreground hover:text-foreground")
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          {candLoading && candidates.length === 0 ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-xl bg-muted/50" />
              ))}
            </div>
          ) : candidates.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                <Icon name="UserPlus" size={20} />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-foreground">
                {search || inviteFilter ? "No matching candidates" : "No candidates yet"}
              </h3>
              <p className="mx-auto mt-1 max-w-xs text-sm text-muted-foreground">
                {search || inviteFilter
                  ? "Try a different search or filter."
                  : "Add candidates to build your funnel, then invite them to interview."}
              </p>
              {canWrite && !search && !inviteFilter && job.status !== "closed" && (
                <Button className="mt-4 bg-emerald-700 text-white hover:bg-emerald-800" size="sm" iconName="UserPlus" onClick={() => setAddOpen(true)}>
                  Add candidates
                </Button>
              )}
            </div>
          ) : (
            <>
              <ul>
                {candidates.map((cand) => (
                  <CandidateRow
                    key={cand.id}
                    cand={cand}
                    canWrite={canWrite}
                    jobActive={isActive}
                    busyId={rowBusyId}
                    onView={(c) => setDetailId(c.id)}
                    onInvite={handleInviteOne}
                    onResend={handleResend}
                    onWithdraw={(c) => setConfirm({ kind: "withdraw", cand: c })}
                  />
                ))}
              </ul>
              {candidates.length < candTotal && (
                <div className="border-t border-border p-4 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={candLoading}
                    onClick={() => setLimit((l) => l + PAGE_SIZE)}
                  >
                    Load more ({candTotal - candidates.length} remaining)
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {activeTab === "analytics" && (
        <AnalyticsView job={job} analytics={analytics} />
      )}

      {/* Modals */}
      <EditJobModal
        open={editJobOpen}
        onClose={() => setEditJobOpen(false)}
        job={job}
        onSaved={(updated) => {
          setJob(updated);
          setBanner({ type: "success", text: "Job details updated." });
        }}
      />
      <InviteCandidatesModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        jobId={jobId}
        candidates={candidates}
        onInvited={() => {
          setBanner({ type: "success", text: "Candidate invites sent successfully." });
          refreshAll();
        }}
        onOpenAdd={() => setAddOpen(true)}
      />
      <RoundFormModal
        open={!!roundModal}
        onClose={() => setRoundModal(null)}
        jobId={jobId}
        round={roundModal?.round || null}
        onSaved={(updatedJob) => {
          setJob(updatedJob);
          setBanner({
            type: "success",
            text: roundModal?.mode === "create" ? "Round added successfully." : "Round updated successfully.",
          });
          refreshAll();
        }}
      />
      <AddCandidatesModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        jobId={jobId}
        onAdded={refreshAll}
      />
      <CandidateDetailModal
        open={!!detailId}
        onClose={() => setDetailId("")}
        jobId={jobId}
        candidateId={detailId}
        job={job}
        canWrite={canWrite}
        onChanged={refreshAll}
      />
      <RoundReviewModal
        open={!!reviewRoundId}
        onClose={() => setReviewRoundId("")}
        jobId={jobId}
        roundId={reviewRoundId}
        onConfirmed={handleReviewConfirmed}
      />
      <ConfirmDialog
        open={!!advanceRound}
        onClose={() => setAdvanceRound(null)}
        title={advanceRound ? `Advance ${advanceRound.name} now?` : "Advance round now?"}
        description={
          advanceRound
            ? `This runs ${advanceRound.name}'s advancement immediately instead of waiting for its scheduled date. Candidates still in progress or not yet started will be marked no-shows (their reserved credits are released), then ${
                advanceRound.manualGate
                  ? "the round will pause for your review so you choose who advances."
                  : "the round's rule decides who advances and the next round opens automatically."
              } This cannot be undone.`
            : ""
        }
        confirmLabel="Advance now"
        variant="default"
        busy={advanceBusy}
        onConfirm={handleAdvanceNow}
      />
      <ConfirmDialog
        open={confirm?.kind === "withdraw"}
        onClose={() => setConfirm(null)}
        title="Withdraw candidate?"
        description={
          confirm?.cand
            ? `${confirm.cand.name} will be removed from this job. Any credit reserved for an unstarted interview is released back to your wallet. This cannot be undone.`
            : ""
        }
        confirmLabel="Withdraw"
        variant="destructive"
        busy={confirmBusy}
        onConfirm={doWithdraw}
      />
      <ConfirmDialog
        open={confirm?.kind === "delete_round"}
        onClose={() => setConfirm(null)}
        title={confirm?.round ? `Delete ${confirm.round.name}?` : "Delete this round?"}
        description="This will permanently delete this round from the draft pipeline. Subsequent rounds will be re-numbered."
        confirmLabel="Delete round"
        variant="destructive"
        busy={confirmBusy}
        onConfirm={doDeleteRound}
      />
      <ConfirmDialog
        open={confirm?.kind === "close"}
        onClose={() => setConfirm(null)}
        title="Close this job?"
        description="No new interviews will be accepted. Candidates who haven't started will not be able to. You can't reopen a closed job."
        confirmLabel="Close job"
        variant="warning"
        busy={confirmBusy}
        onConfirm={doClose}
      />
      <ConfirmDialog
        open={confirm?.kind === "delete"}
        onClose={() => setConfirm(null)}
        title="Delete this draft?"
        description="This permanently deletes the job and its rounds. This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        busy={confirmBusy}
        onConfirm={doDelete}
      />
    </div>
  );
}
